import { useState, useEffect, useRef, createContext, useContext } from "react";
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format as formatDate, parseISO, isWithinInterval, format } from 'date-fns';
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, MapPin, Users, Car, Fuel, Settings, Star, Search, Filter, CalendarDays, ChevronLeft, ChevronRight, ChevronDown, Menu, X, Instagram, Facebook } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { toast } from "sonner";

// Language Context
const LanguageContext = createContext();

// Currency Context
const CurrencyContext = createContext();

const USD_TO_EUR_RATE = 0.85;
const USD_TO_XCG_RATE = 2.12;

// Display-only XCG daily rate overrides (keep USD/EUR unchanged)
const XCG_DAILY_RATE_OVERRIDES = {
  'Budget Explorer': { low: 65, high: 69 },
  'Island Cruiser': { low: 77, high: 84 },
  'Coastal Comfort': { low: 67, high: 73 },
  'Beach Runner': { low: 67, high: 73 }
};


// Translations
const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      cars: "Cars", 
      calendar: "Calendar",
      about: "About",
      contact: "Contact",
      bookNow: "Book Now",
      language: "Language"
    },
    // Hero Section
    hero: {
      title: "Paradise Found!",
      subtitle: "Your adventure in Curaçao starts with CuraCruze",
      cta1: "Start My Adventure!",
      cta2: "Discover Paradise",
      description: "Chase sunsets, dive into hidden beaches, and cruise the island like a local - your Curaçao adventure starts the moment you grab the keys.",
      tagline: "Sun, sea, and spontaneous detours… let’s go!",
      vacationBadge: "Your Paradise Awaits!",
      driveYourDreams: "Drive Your Dreams",
      chips: {
        sun: "Sun",
        beaches: "Beaches",
        adventure: "Adventure"
      },
      memoriesWaiting: "Memories Waiting",
      stats: {
        sunnyDays: "Sunny Days/Year",
        beaches: "Pristine Beaches",
        experiences: "Unique Experiences", 
        satisfied: "Happy Customers"
      }
    },
    // Cars Section
    cars: {
      sectionTitle: "Your Vacation Starts Here!",
      sectionSubtitle: "Ready to turn your Curaçao dreams into reality? We've got everything you need for the perfect island getaway!",
      fleetTitle: "Our Fleet",
      fleetSubtitle: "The Keys To Freedom",
      viewAll: "View All Cars",
      bookNow: "Book Now",
      available: "Available",
      unavailable: "Unavailable", 
      nextAvailable: "Next Available",
      seats: "seats",
      lowSeason: "Low Season",
      highSeason: "High Season",
      weeklyFrom: "Weekly from",
      loading: "Loading our amazing fleet...",
      compact: "Compact",
      suv: "SUV", 
      sedan: "Sedan",
      automatic: "Automatic",
      manual: "Manual",
      availability: "Availability"
    },
    carsPage: {
      bookingInfo: {
        title: "Booking Information",
        bookWithUs: "Book With Us",
        whatsapp: "WhatsApp",
        email: "Email",
        reservationFee: "Reservation Fee: XCG 150 (deducted from total rental price)",
        cancellationPolicy: "Cancellation Policy",
        cancel48: "Free cancellation up to 48 hours before pick-up",
        cancel48to24: "Cancel within 48 hrs = 50% of reservation fee lost",
        cancel24: "Cancel within 24 hrs = 100% of reservation fee lost",
        rentalTerms: "Rental Terms",
        termNoDeposit: "No deposit required, just the reservation fee",
        termCleanFullTank: "Cars delivered clean and with a full tank – return the same",
        termInsured: "All vehicles are well insured",
        termPickupDropoff: "Pick-up/drop-off service available",
        termExtras: "Extras like child seats or additional driver on request."
      }
    },
    carDescriptions: {
      budgetExplorer: "Perfect for budget-conscious travelers. This clean white Nissan Versa sedan delivers reliable, fuel-efficient comfort for cruising all around Curaçao. CDW insurance included.",
      islandCruiser: "Stand out with this vibrant blue Kia Soul ideal for beach hopping and scenic drives across Curaçao. Enjoy a roomy interior and a fun, easy ride. CDW insurance included.",
      coastalComfort: "Elegant white Kia Rio sedan, pictured by Curaçao’s stunning coastline. Comfortable, stylish, and ready for every island adventure. CDW insurance included.",
      beachRunner: "The ultimate beach companion! This compact white Chevrolet Spark is perfect for city driving and effortless parking near Curaçao’s most popular beaches. CDW insurance included."
    },
    // Secret Paradise Spots
    paradise: {
      title: "Secret Paradise Spots!",
      subtitle: "Discover hidden gems that only locals know about",
      hiddenBeaches: {
        title: "Hidden Beaches",
        description: "Secluded white sand beaches perfect for romantic getaways and peaceful relaxation.",
        locations: "Playa Lagun: Secluded cove with turtles • Playa Jeremi: Peaceful and breathtaking • Playa Gipy: Island's best-kept secret • Playa Daaibari: Quiet northern coast gem"
      },
      secretSnorkeling: {
        title: "Secret Snorkeling",
        description: "Pristine underwater worlds with colorful coral reefs and tropical fish.",
        locations: "Playa Piskado: Snorkel with sea turtles • Tugboat Beach: Sunken tugboat adventure • Playa Kalki: Vibrant coral formations • Playa Santu Pretu: Shipwreck diving spot"
      },
      magicalSunsets: {
        title: "Magical Sunsets",
        description: "Breathtaking sunset viewpoints for unforgettable golden hour moments.",
        locations: "Knip Beach: Turquoise water sunsets • Mambo Beach: Lively evening atmosphere • Shete Boka: Dramatic coastline views • Klein Curacao: Uninhabited island paradise"
      },
      cta: "Rent a Car & Explore These Hidden Gems!",
      tip: "*Ask our team for detailed directions and insider tips when you pick up your car"
    },
    // Popular Destinations
    destinations: {
      title: "Popular Destinations",
      subtitle: "Must-visit spots around the island",
      pundaOtrobanda: {
        title: "Punda-Otrobanda",
        name: "Punda-Otrobanda",
        description: "Historic Willemstad with colorful Dutch colonial architecture and UNESCO World Heritage status."
      },
      janthiel: {
        title: "Janthiel Beach",
        name: "Janthiel", 
        description: "Pristine beach with crystal clear waters, perfect for swimming and snorkeling."
      },
      mambo: {
        title: "Mambo Beach",
        name: "Mambo Beach",
        description: "Vibrant beach club scene with restaurants, bars, and water sports activities."
      }
    },
    // Reviews Section
    reviews: {
      title: "What Our Customers Say",
      subtitle: "Real experiences from happy travelers",
      trustpilot: "Excellent • 4.8/5 • Based on 200+ reviews",
      testimonials: {
        thijs: {
          quote: "Local knowledge is priceless! The team shared secret beaches and amazing restaurants we never would have found. The car was perfect and the service was world-class.",
          name: "Thijs Hendriks",
          role: "Photography Enthusiast"
        },
        eva: {
          quote: "Clean cars, fair prices, and genuine care for customers. CuraCruze turned our vacation into an adventure! Already planning our next trip and will definitely book with them again.",
          name: "Eva de Jong", 
          role: "Repeat Customer"
        },
        lars: {
          quote: "Professional service from start to finish. The car was immaculate and exactly as described. Great value for money. Will definitely use CuraCruze again!",
          name: "Lars van Dam",
          role: "Business Traveler"
        },
        anna: {
          quote: "Exceeded our expectations! The staff gave us fantastic recommendations for hidden beaches and local restaurants. The car was spotless and the service was outstanding.",
          name: "Anna Jansen",
          role: "Couple's Getaway"
        },
        larsB: {
          quote: "Best car rental experience ever! The Chevrolet Spark was perfect for city driving and parking at the beaches. Free pickup and drop-off made everything so convenient.",
          name: "Lars Bakker",
          role: "Adventure Seeker"
        },
        sophie: {
          quote: "CuraCruze made our family vacation perfect! The Kia Rio was spacious and comfortable. The child seats were clean and properly installed. Excellent service all around!",
          name: "Sophie Mulder",
          role: "Family Vacation"
        },
        daan: {
          quote: "Transparent pricing and no hidden fees - exactly what they promised! The booking process was smooth and the staff was incredibly helpful with local tips and directions.",
          name: "Daan van Leeuwen",
          role: "First-time Visitor"
        },
        lotte: {
          quote: "24/7 support was a lifesaver! Had a minor issue late at night and they resolved it immediately. True professionals who care about their customers. Thank you CuraCruze!",
          name: "Lotte Smit",
          role: "Digital Nomad"
        }
      }
    },
    // FAQ Section
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about renting with CuraCruze",
      questions: {
        booking: {
          question: "How do I book a car with CuraCruze?",
          answer: "Simply browse our fleet, select your preferred vehicle, choose your dates, and complete the secure payment process. You'll receive instant confirmation via email with pickup instructions."
        },
        requirements: {
          question: "What are the requirements to rent a car?",
          answer: "You must be at least 21 years old with a valid driver's license held for minimum 1 year. International visitors need a valid passport and international driving permit."
        },
        payment: {
          question: "What payment methods do you accept?",
          answer: "We accept credit cards (Visa, Mastercard) and iDEAL (EUR) through Stripe Checkout."
        },
        insurance: {
          question: "Do CuraCruze cars come with insurance?",
          answer: "Yes, we have insurance coverage, is included with every rental."
        },
        fuel: {
          question: "What is your fuel policy?",
          answer: "All cars are provided with a full tank and should be returned with a full tank. There's a gas station conveniently located 2 minutes from our pickup location."
        },
        pickup: {
          question: "Where can I pick up and drop off the car?",
          answer: "We offer flexible pickup and drop-off options including airports, city hubs, and even door-to-door delivery in select areas. Check availability during booking."
        }
      }
    },
    // Service Excellence Section
    services: {
      title: "CuraCruze Service Excellence",
      subtitle: "Everything you need for a perfect car rental experience in Curaçao",
      insurance: {
        title: "Cars Insurance",
        description: "Every CuraCruze vehicle comes with full insurance coverage included, so you can enjoy your journey stress-free."
      },
      pickup: {
        title: "Pick-up & Drop-off Service",
        description: "At CuraCruze, you'll enjoy free pick-up & drop-off service, whether at Hato Airport or your accommodation. Your comfort is our priority."
      },
      service: {
        title: "Great Service", 
        description: "At CuraCruze, we pride ourselves on delivering friendly, reliable, and professional service – every step of the way. Count on us for excellent, hassle-free service from start to finish."
      },
      reservation: {
        title: "Reservation",
        description: "We require a reservation of at least 3 days in advance. The cost of the reservation is XCG 150,-. This amount will be deducted from the total price."
      },
      cleanCar: {
        title: "Clean Car - Full Tank",
        description: "We provide the car clean and with a full tank. We would greatly appreciate it if the car is returned in the same condition   clean and with a full tank."
      },
      family: {
        title: "Travel with Family",
        description: "We provide child seats suitable for children aged 3 to 12 years. The cost is XCG 5,- per seat per day."
      }
    },
    // About Section
    about: {
      title: "About CuraCruze",
      subtitle: "Your trusted car rental partner in Curaçao",
      experience: "Over 10 Years Experience",
      satisfaction: "99% Customer Satisfaction",
      vehicles: "Premium Vehicle Fleet", 
      support: "24/7 Customer Support",
      story: "Founded in 2014, CuraCruze has been helping travelers explore the beautiful island of Curaçao with reliable, affordable car rentals.",
      mission: "We know Curaçao like the back of our hand and are happy to share tips on the best beaches, restaurants, and hidden gems.",
      cta: "Book your rental car today and explore Curaçao at your own pace."
    },
    aboutPage: {
      hero: {
        title: "About CuraCruze",
        subtitle: "Your adventure in Curaçao starts with CuraCruze"
      },
      intro: {
        title: "CuraCruze Auto Rental",
        description: "Curacruze is a Dutch sole proprietorship (Chamber of Commerce no. 86655124), legally established in the Netherlands. From the Netherlands, we offer car rental services on Curaçao for travelers seeking a reliable and transparent rental experience.\n\nCar rentals on Curaçao are provided in cooperation with carefully selected local partners. Curacruze acts as the service provider and primary point of contact for customers, while the vehicles are made available locally by our partners.\n\nAll bookings and payments are processed through our Dutch business entity. We ensure clear communication, support before arrival, and assistance throughout the rental period, allowing our customers to enjoy a worry free stay on Curaçao.\n\nIf you have any questions, please feel free to contact us   we are happy to assist you."
      },
      why: {
        title: "Why Choose CuraCruze?",
        reliable: {
          title: "Reliable Service",
          description: "We're available 24/7. From the moment you land to your final ride to the airport, we've got you covered."
        },
        transparent: {
          title: "Transparent Pricing",
          description: "No hidden fees. Clear agreements and fair rates."
        },
        pickup: {
          title: "Free Pick-up and Drop-off Service",
          description: "We'll deliver your rental car to your accommodation or the airport, free of charge."
        },
        vehicles: {
          title: "Modern, Well-maintained Vehicles",
          description: "Our cars are clean, fully insured, and regularly serviced for your peace of mind."
        },
        local: {
          title: "Local and Personal",
          description: "We know Curaçao like the back of our hand and are happy to share tips on the best beaches, restaurants, and hidden gems."
        },
        closing: {
          line1: "Book your rental car today and explore Curaçao at your own pace.",
          line2: "Got questions? Don't hesitate to contact us   we're happy to help!"
        }
      },
      mission: {
        title: "Our Mission",
        description: "At CuraCruze, it's not just about cars   it's about the experience. We want you to feel free, safe, and right at home on the island. That's why we go the extra mile for you."
      },
      serve: {
        title: "Who We Serve",
        description: "Traveling solo, as a couple, with family, or a group of friends? We've got compact cars, spacious SUVs, and everything in between. Planning a longer stay? Ask about our extended rental discounts."
      },
      cta: {
        title: "Ready to Explore Curaçao?",
        subtitle: "Contact us today to book your perfect rental car and start your island adventure!",
        browseFleet: "Browse Our Fleet",
        contactUs: "Contact Us"
      }
    },
    // Contact Section
    contact: {
      title: "Ready to Explore Curaçao?",
      subtitle: "Get in touch with our friendly team",
      phone: "Phone",
      email: "Email",
      location: "Location", 
      hours: "Business Hours",
      address: "Willemstad, Curaçao",
      legalBusinessAddress: "Legal business address",
      legalBusinessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      businessHours: "Mon-Sun: 8:00 AM - 8:00 PM",
      cta: "Contact Us Today"
    },
    // Footer
    footer: {
      quickLinks: "Quick Links",
      contact: "Contact Info",
      followUs: "Follow Us",
      businessAddress: "Business address",
      businessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      phoneNumber: "+31 6 51929275",
      aboutUs: "About Us",
      ourFleet: "Our Fleet",
      bookingTerms: "Booking Terms", 
      privacy: "Privacy Policy",
      pricing: "Pricing",
      locations: "Locations",
      exploreCuracao: "Explore Curaçao",
      beaches: "Beaches",
      divingSpots: "Diving Spots",
      culturalSites: "Cultural Sites",
      restaurants: "Restaurants",
      copyright: "© 2024 CuraCruze. All rights reserved.",
      description: "Your trusted car rental partner in Curaçao. Explore the island with our premium fleet of vehicles and exceptional customer service.",
      legalNote: "Curacruze is a Dutch sole proprietorship (KvK 86655124), registered in the Netherlands. We offer car rentals in Curaçao as a Dutch service provider, in collaboration with local partners.",
      termsTitle: "Terms & Conditions",
      termsContent: "1. Company Information\n\nCuracruze is a Dutch sole proprietorship, legally established in the Netherlands and registered with the Dutch Chamber of Commerce under number 86655124.\n\nCuracruze offers car rental services on Curaçao in cooperation with local rental partners. Curacruze acts as the contractual service provider and primary point of contact for customers.\n\n2. Scope of Services\n\nCuracruze provides online car rental booking services for vehicles located on Curaçao. The vehicles are supplied and operated locally by carefully selected third-party rental partners.\n\nCuracruze is responsible for:\n• processing bookings and payments\n• customer communication and support\n• coordinating the rental with local partners\n\nThe local partner is responsible for:\n• providing the vehicle\n• vehicle condition and availability\n• local delivery and return procedures\n\n3. Bookings and Payments\n\nAll bookings are made through the Curacruze website. All payments are processed through Curacruze’s Dutch business entity using approved payment service providers.\n\nA booking is considered confirmed once payment has been successfully completed and a confirmation email has been sent.\n\n4. Rental Agreement\n\nUpon vehicle pickup, the customer may be required to sign a local rental agreement with the local partner. This agreement may include additional terms relating to vehicle use, insurance, deposits, and local regulations.\n\nIn case of conflict, these Terms & Conditions govern the relationship between the customer and Curacruze.\n\n5. Driver Requirements\n\nUnless stated otherwise, drivers must:\n• be at least 21 years old\n• hold a valid driving license\n• present valid identification at pickup\n\nAdditional requirements may apply depending on the local partner.\n\n6. Insurance and Liability\n\nInsurance coverage is subject to the terms provided at the time of booking and the local partner’s policy.\n\nCuracruze is not liable for:\n• damages caused by misuse of the vehicle\n• violations of local traffic laws\n• fines, penalties, or confiscations\n\nThe customer remains responsible for the vehicle during the rental period.\n\n7. Cancellations and Refunds\n\nCancellation and refund policies are clearly stated during the booking process.\n\nRefunds, if applicable, are processed through the original payment method and handled by Curacruze’s Dutch business entity.\n\n8. Complaints and Support\n\nQuestions or complaints should be submitted directly to Curacruze via the contact details provided on the website. Curacruze will coordinate with the local partner where necessary.\n\n9. Governing Law\n\nThese Terms & Conditions are governed by Dutch law. Any disputes shall be submitted to the competent court in the Netherlands, unless mandatory local law provides otherwise.\n\n10. Amendments\n\nCuracruze reserves the right to amend these Terms & Conditions at any time. The version published on the website at the time of booking shall apply.\n\nContact Information\nCuracruze\nDutch sole proprietorship\nChamber of Commerce no. 86655124\nThe Netherlands.",
      privacyTitle: "Privacy Policy",
      privacyContent: "1. Introduction\n\nCuracruze respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and protect personal information when you visit our website or use our services.\n\nCuracruze is a Dutch sole proprietorship, legally established in the Netherlands and registered with the Dutch Chamber of Commerce under number 86655124.\n\n2. Personal Data We Collect\n\nWe may collect and process the following personal data:\n• Full name\n• Email address\n• Phone number\n• Billing and payment information\n• Booking details (rental dates, vehicle preferences, location)\n• Communication with customer support\n• IP address and browser information (for security and basic analytics)\n\n3. Purpose of Data Processing\n\nWe process personal data for the following purposes:\n• Handling car rental bookings\n• Processing payments and refunds\n• Communicating with customers before and during the rental period\n• Providing customer support\n• Complying with legal and regulatory obligations\n• Preventing fraud and misuse of our services\n\n4. Payments\n\nPayments made through our website are processed via secure third-party payment service providers. Curacruze does not store full payment card details.\n\nAll payments are processed through Curacruze’s Dutch business entity.\n\n5. Data Sharing\n\nPersonal data may be shared only when necessary:\n• With local rental partners on Curaçao, solely for the purpose of executing the car rental\n• With payment service providers for transaction processing\n• With authorities when legally required\n\nWe do not sell or rent personal data to third parties.\n\n6. International Data Transfers\n\nSome personal data may be shared with service partners located on Curaçao. In such cases, Curacruze ensures that appropriate safeguards are in place to protect personal data in accordance with applicable data protection laws.\n\n7. Data Retention\n\nPersonal data is retained only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or to comply with legal obligations.\n\n8. Data Security\n\nCuracruze takes appropriate technical and organizational measures to protect personal data against unauthorized access, loss, or misuse.\n\n9. Your Rights\n\nUnder applicable data protection laws, you have the right to:\n• Access your personal data\n• Request correction or deletion\n• Object to or restrict processing\n• Request data portability\n\nRequests can be submitted using the contact details below.\n\n10. Cookies\n\nCuracruze uses cookies to ensure proper website functionality and improve user experience. Detailed information about cookies may be provided in a separate Cookie Policy.\n\n11. Changes to This Policy\n\nCuracruze reserves the right to update this Privacy Policy at any time. The version published on the website at the time of use shall apply.\n\n12. Contact Information\n\nCuracruze\nDutch sole proprietorship\nChamber of Commerce no. 86655124\nThe Netherlands\n\nFor privacy-related questions, please contact us via the contact details provided on the website."
    },
    // Common
    common: {
      loading: "Loading...",
      error: "Something went wrong",
      tryAgain: "Try Again",
      close: "Close",
      next: "Next",
      previous: "Previous",
      select: "Select",
      cancel: "Cancel",
      confirm: "Confirm",
      save: "Save",
      readMore: "Read more",
      readLess: "Read less"
    }
  },
  nl: {
    // Navigation
    nav: {
      home: "Home",
      cars: "Auto's",
      calendar: "Kalender",
      about: "Over Ons",
      contact: "Contact", 
      bookNow: "Boek Nu",
      language: "Taal"
    },
    // Hero Section
    hero: {
      title: "Paradijs Gevonden!",
      subtitle: "Jouw avontuur in Curaçao begint met CuraCruze",
      cta1: "Start Mijn Avontuur!",
      cta2: "Ontdek Het Paradijs",
      description: "Jaag zonsondergangen na, duik naar verborgen stranden en cruise het eiland als een local - jouw Curaçao-avontuur begint zodra je de sleutels pakt.",
      tagline: "Zon, zee en spontane omweggetjes… let's go!",
      vacationBadge: "Jouw Paradijs Wacht!",
      driveYourDreams: "Rijd Je Dromen",
      chips: {
        sun: "Zon",
        beaches: "Stranden",
        adventure: "Avontuur"
      },
      memoriesWaiting: "Herinneringen Wachten",
      stats: {
        sunnyDays: "Zonnige Dagen/Jaar",
        beaches: "Pristine Stranden",
        experiences: "Unieke Ervaringen",
        satisfied: "Tevreden Klanten"
      }
    },
    // Cars Section
    cars: {
      sectionTitle: "Jouw Vakantie Begint Hier!",
      sectionSubtitle: "Klaar om je Curaçao dromen werkelijkheid te maken? We hebben alles wat je nodig hebt voor de perfecte eilandvakantie!",
      fleetTitle: "Onze Vloot",
      fleetSubtitle: "De Sleutels Tot Vrijheid",
      viewAll: "Bekijk Alle Auto's",
      bookNow: "Boek Nu",
      available: "Beschikbaar",
      unavailable: "Niet Beschikbaar",
      nextAvailable: "Volgende Beschikbaar", 
      seats: "stoelen",
      lowSeason: "Laag Seizoen",
      highSeason: "Hoog Seizoen",
      weeklyFrom: "Wekelijks vanaf",
      loading: "Onze geweldige vloot laden...",
      compact: "Compact",
      suv: "SUV",
      sedan: "Sedan", 
      automatic: "Automaat",
      manual: "Handmatig",
      availability: "Beschikbaarheid"
    },
    carsPage: {
      bookingInfo: {
        title: "Boekingsinformatie",
        bookWithUs: "Boek Bij Ons",
        whatsapp: "WhatsApp",
        email: "E-mail",
        reservationFee: "Reserveringskosten: XCG 150 (wordt verrekend met de totale huurprijs)",
        cancellationPolicy: "Annuleringsbeleid",
        cancel48: "Gratis annuleren tot 48 uur vóór ophalen",
        cancel48to24: "Annuleren binnen 48 uur = 50% van de reserveringskosten vervalt",
        cancel24: "Annuleren binnen 24 uur = 100% van de reserveringskosten vervalt",
        rentalTerms: "Huurvoorwaarden",
        termNoDeposit: "Geen borg nodig, alleen de reserveringskosten",
        termCleanFullTank: "Auto’s worden schoon en met volle tank geleverd – graag hetzelfde terugbrengen",
        termInsured: "Alle voertuigen zijn goed verzekerd",
        termPickupDropoff: "Ophaal-/terugbrengservice beschikbaar",
        termExtras: "Extra’s zoals kinderzitjes of een extra bestuurder op aanvraag."
      }
    },
    carDescriptions: {
      budgetExplorer: "Perfect voor budgetbewuste reizigers. Deze schone witte Nissan Versa sedan biedt betrouwbare, zuinige comfort om heel Curaçao te verkennen. CDW-verzekering inbegrepen.",
      islandCruiser: "Val op met deze levendige blauwe Kia Soul ideaal voor strandhoppen en schilderachtige ritten over Curaçao. Geniet van een ruim interieur en een fijne, makkelijke rit. CDW-verzekering inbegrepen.",
      coastalComfort: "Elegante witte Kia Rio sedan, gefotografeerd langs de prachtige kustlijn van Curaçao. Comfortabel, stijlvol en klaar voor elk eilandavontuur. CDW-verzekering inbegrepen.",
      beachRunner: "De ultieme strandbuddy! Deze compacte witte Chevrolet Spark is perfect voor stadsritten en gemakkelijk parkeren bij de populairste stranden van Curaçao. CDW-verzekering inbegrepen."
    },
    // Secret Paradise Spots
    paradise: {
      title: "Geheime Paradijs Plekken!",
      subtitle: "Ontdek verborgen parels die alleen locals kennen",
      hiddenBeaches: {
        title: "Verborgen Stranden",
        description: "Afgelegen witte zandstranden perfect voor romantische uitjes en vredige ontspanning.",
        locations: "Playa Lagun: Afgelegen baai met schildpadden • Playa Jeremi: Vredig en adembenemend • Playa Gipy: Het best bewaarde geheim van het eiland • Playa Daaibari: Rustig noordkust juweeltje"
      },
      secretSnorkeling: {
        title: "Geheim Snorkelen",
        description: "Ongerepte onderwaterwerelden met kleurrijke koraalriffen en tropische vissen.",
        locations: "Playa Piskado: Snorkelen met zeeschildpadden • Tugboat Beach: Gezonken sleepboot avontuur • Playa Kalki: Levendige koraalformaties • Playa Santu Pretu: Scheepswrak duikplek"
      },
      magicalSunsets: {
        title: "Magische Zonsondergangen", 
        description: "Adembenemende zonsondergang uitkijkpunten voor onvergetelijke golden hour momenten.",
        locations: "Knip Beach: Turquoise water zonsondergangen • Mambo Beach: Levendige avond sfeer • Shete Boka: Dramatische kustlijn uitzichten • Klein Curacao: Onbewoond eiland paradijs"
      },
      cta: "Huur een Auto & Ontdek Deze Verborgen Parels!",
      tip: "*Vraag ons team om gedetailleerde routebeschrijvingen en insider tips wanneer je je auto ophaalt"
    },
    // Popular Destinations
    destinations: {
      title: "Populaire Bestemmingen", 
      subtitle: "Must-visit plekken rond het eiland",
      pundaOtrobanda: {
        title: "Punda-Otrobanda",
        name: "Punda-Otrobanda",
        description: "Historisch Willemstad met kleurrijke Nederlandse koloniale architectuur en UNESCO Werelderfgoed status."
      },
      janthiel: {
        title: "Janthiel Beach",
        name: "Janthiel",
        description: "Ongerept strand met kristalhelder water, perfect om te zwemmen en snorkelen."
      },
      mambo: {
        title: "Mambo Beach",
        name: "Mambo Beach", 
        description: "Levendige strandclub scene met restaurants, bars en watersport activiteiten."
      }
    },
    // Reviews Section
    reviews: {
      title: "Wat Onze Klanten Zeggen", 
      subtitle: "Echte ervaringen van tevreden reizigers",
      trustpilot: "Uitstekend • 4,8/5 • Gebaseerd op 200+ beoordelingen",
      testimonials: {
        thijs: {
          quote: "Lokale kennis is onbetaalbaar! Het team deelde geheime stranden en geweldige restaurants die we nooit zouden hebben gevonden. De auto was perfect en de service was wereldklasse.",
          name: "Thijs Hendriks",
          role: "Fotografie Liefhebber"
        },
        eva: {
          quote: "Schone auto's, eerlijke prijzen en oprechte zorg voor klanten. CuraCruze maakte van onze vakantie een avontuur! Al plannen we onze volgende reis en boeken zeker weer bij hen.",
          name: "Eva de Jong", 
          role: "Terugkerende Klant"
        },
        lars: {
          quote: "Professionele service van begin tot eind. De auto was onberispelijk en precies zoals beschreven. Uitstekende prijs-kwaliteitverhouding. Zeker weer CuraCruze gebruiken!",
          name: "Lars van Dam",
          role: "Zakenreiziger"
        },
        anna: {
          quote: "Onze verwachtingen werden overtroffen! Het team gaf ons fantastische tips voor verborgen stranden en lokale restaurants. De auto was brandschoon en de service was top.",
          name: "Anna Jansen",
          role: "Romantische Trip"
        },
        larsB: {
          quote: "Beste autoverhuurervaring ooit! De Chevrolet Spark was perfect voor stadsritten en parkeren bij de stranden. Gratis ophalen en terugbrengen maakte alles super makkelijk.",
          name: "Lars Bakker",
          role: "Avonturier"
        },
        sophie: {
          quote: "CuraCruze maakte onze gezinsvakantie perfect! De Kia Rio was ruim en comfortabel. De kinderzitjes waren schoon en netjes geïnstalleerd. Echt geweldige service.",
          name: "Sophie Mulder",
          role: "Gezinsvakantie"
        },
        daan: {
          quote: "Transparante prijzen en geen verborgen kosten   precies zoals beloofd! Het boekingsproces verliep soepel en het team hielp enorm met lokale tips en routebeschrijvingen.",
          name: "Daan van Leeuwen",
          role: "Eerste Bezoek"
        },
        lotte: {
          quote: "24/7 support was een lifesaver! Laat op de avond een klein probleem en ze losten het meteen op. Echte professionals die om hun klanten geven. Dankjewel CuraCruze!",
          name: "Lotte Smit",
          role: "Digital Nomad"
        }
      }
    },
    // FAQ Section
    faq: {
      title: "Veelgestelde Vragen",
      subtitle: "Alles wat je moet weten over huren bij CuraCruze",
      questions: {
        booking: {
          question: "Hoe boek ik een auto bij CuraCruze?",
          answer: "Bladeren gewoon door onze vloot, selecteer je gewenste voertuig, kies je datums en voltooi het veilige betalingsproces. Je ontvangt directe bevestiging via e-mail met ophalinstructies."
        },
        requirements: {
          question: "Wat zijn de vereisten om een auto te huren?",
          answer: "Je moet minimaal 21 jaar oud zijn met een geldig rijbewijs dat minimaal 1 jaar gehouden is. Internationale bezoekers hebben een geldig paspoort en internationaal rijbewijs nodig."
        },
        payment: {
          question: "Welke betaalmethoden accepteren jullie?",
          answer: "We accepteren creditcards (Visa, Mastercard) en iDEAL (EUR) via Stripe Checkout."
        },
        insurance: {
          question: "Komen CuraCruze auto's met verzekering?",
          answer: "Ja, we hebben verzekeringsdekking; deze is inbegrepen bij elke huur."
        },
        fuel: {
          question: "Wat is jullie brandstofbeleid?",
          answer: "Alle auto's worden geleverd met volle tank en moeten worden teruggebracht met volle tank. Er is een tankstation op 2 minuten van onze ophaallocatie."
        },
        pickup: {
          question: "Waar kan ik de auto ophalen en terugbrengen?",
          answer: "We bieden flexibele ophaal- en terugbrengopties, inclusief luchthavens, stadslocaties en in geselecteerde gebieden zelfs deur-tot-deur bezorging. Check de beschikbaarheid tijdens het boeken."
        }
      }
    },
    // Service Excellence Section
    services: {
      title: "CuraCruze Service Uitmuntendheid",
      subtitle: "Alles wat je nodig hebt voor een perfecte autoverhuur ervaring in Curaçao",
      insurance: {
        title: "Auto Verzekering",
        description: "Elke CuraCruze voertuig komt met volledige verzekeringsdekking inbegrepen, zodat je stressvrij van je reis kunt genieten."
      },
      pickup: {
        title: "Ophaal & Breng Service",
        description: "Bij CuraCruze geniet je van gratis ophaal & breng service, of het nu op Hato Airport of bij je accommodatie is. Jouw comfort is onze prioriteit."
      },
      service: {
        title: "Geweldige Service",
        description: "Bij CuraCruze zijn we er trots op om vriendelijke, betrouwbare en professionele service te leveren – elke stap van de weg. Reken op ons voor uitstekende, zorgeloze service van begin tot eind."
      },
      reservation: {
        title: "Reservering",
        description: "We vereisen een reservering van minimaal 3 dagen van tevoren. De kosten van de reservering zijn XCG 150,-. Dit bedrag wordt afgetrokken van de totaalprijs."
      },
      cleanCar: {
        title: "Schone Auto - Volle Tank", 
        description: "We leveren de auto schoon en met een volle tank. We zouden het zeer waarderen als de auto wordt teruggebracht in dezelfde staat   schoon en met een volle tank."
      },
      family: {
        title: "Reizen met Familie",
        description: "We bieden kinderstoeltjes geschikt voor kinderen van 3 tot 12 jaar. De kosten zijn XCG 5,- per stoel per dag."
      }
    },
    // About Section
    about: {
      title: "Over CuraCruze",
      subtitle: "Jouw vertrouwde autoverhuur partner in Curaçao", 
      experience: "Meer dan 10 Jaar Ervaring",
      satisfaction: "99% Klanttevredenheid",
      vehicles: "Premium Voertuig Vloot",
      support: "24/7 Klantenservice",
      story: "Opgericht in 2014, helpt CuraCruze reizigers het prachtige eiland Curaçao te verkennen met betrouwbare, betaalbare autoverhuur.",
      mission: "We kennen Curaçao als onze broekzak en delen graag tips over de beste stranden, restaurants en verborgen parels.",
      cta: "Boek vandaag je huurauto en verken Curaçao op je eigen tempo."
    },
    aboutPage: {
      hero: {
        title: "Over CuraCruze",
        subtitle: "Jouw avontuur in Curaçao begint met CuraCruze"
      },
      intro: {
        title: "CuraCruze Autoverhuur",
        description: "Curacruze is een Nederlandse eenmanszaak (Kamer van Koophandel nr. 86655124), juridisch gevestigd in Nederland. Vanuit Nederland bieden wij autoverhuur op Curaçao aan voor reizigers die op zoek zijn naar een betrouwbare en transparante huurervaring.\n\nAutoverhuur op Curaçao wordt aangeboden in samenwerking met zorgvuldig geselecteerde lokale partners. Curacruze treedt op als dienstverlener en eerste aanspreekpunt voor klanten, terwijl de voertuigen lokaal door onze partners beschikbaar worden gesteld.\n\nAlle boekingen en betalingen worden verwerkt via onze Nederlandse onderneming. Wij zorgen voor duidelijke communicatie, ondersteuning vóór aankomst en hulp gedurende de huurperiode, zodat onze klanten zorgeloos van Curaçao kunnen genieten.\n\nHeb je vragen? Neem gerust contact met ons op   we helpen je graag."
      },
      why: {
        title: "Waarom CuraCruze?",
        reliable: {
          title: "Betrouwbare Service",
          description: "We zijn 24/7 bereikbaar. Vanaf het moment dat je landt tot je laatste rit naar het vliegveld: wij regelen het."
        },
        transparent: {
          title: "Transparante Prijzen",
          description: "Geen verborgen kosten. Duidelijke afspraken en eerlijke tarieven."
        },
        pickup: {
          title: "Gratis Ophaal- en Terugbrengservice",
          description: "We leveren je huurauto gratis af bij je accommodatie of op het vliegveld."
        },
        vehicles: {
          title: "Moderne, Goed Onderhouden Auto’s",
          description: "Onze auto’s zijn schoon, goed verzekerd en regelmatig onderhouden voor jouw gemoedsrust."
        },
        local: {
          title: "Lokaal en Persoonlijk",
          description: "We kennen Curaçao als onze broekzak en delen graag tips over de mooiste stranden, restaurants en verborgen parels."
        },
        closing: {
          line1: "Boek vandaag je huurauto en ontdek Curaçao in je eigen tempo.",
          line2: "Vragen? Neem gerust contact met ons op   we helpen je graag!"
        }
      },
      mission: {
        title: "Onze Missie",
        description: "Bij CuraCruze draait het niet alleen om auto’s   het draait om de beleving. We willen dat jij je vrij en veilig voelt en je helemaal thuis voelt op het eiland. Daarom doen we net dat extra stapje voor je."
      },
      serve: {
        title: "Voor Wie Wij Er Zijn",
        description: "Reis je alleen, met z’n tweeën, met familie of met vrienden? We hebben compacte auto’s, ruime SUV’s en alles daartussenin. Blijf je langer? Vraag naar onze kortingen voor langere huurperiodes."
      },
      cta: {
        title: "Klaar om Curaçao te Ontdekken?",
        subtitle: "Neem vandaag contact op, boek jouw ideale huurauto en start je eilandavontuur!",
        browseFleet: "Bekijk Onze Vloot",
        contactUs: "Neem Contact Op"
      }
    },
    // Contact Section
    contact: {
      title: "Klaar om Curaçao te Verkennen?",
      subtitle: "Neem contact op met ons vriendelijke team",
      phone: "Telefoon",
      email: "E-mail",
      location: "Locatie", 
      hours: "Openingstijden",
      address: "Willemstad, Curaçao",
      legalBusinessAddress: "Juridisch bedrijfsadres",
      legalBusinessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      businessHours: "Ma-Zo: 8:00 - 20:00",
      cta: "Neem Vandaag Contact Op"
    },
    // Footer
    footer: {
      quickLinks: "Snelle Links",
      contact: "Contact Info",
      followUs: "Volg Ons",
      businessAddress: "Bedrijfsadres",
      businessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      phoneNumber: "+31 6 51929275",
      aboutUs: "Over Ons",
      ourFleet: "Onze Vloot", 
      bookingTerms: "Boekingsvoorwaarden",
      privacy: "Privacybeleid",
      pricing: "Prijzen",
      locations: "Locaties",
      exploreCuracao: "Ontdek Curaçao",
      beaches: "Stranden",
      divingSpots: "Duikplekken",
      culturalSites: "Culturele bezienswaardigheden",
      restaurants: "Restaurants",
      copyright: "© 2024 CuraCruze. Alle rechten voorbehouden.",
      description: "Jouw vertrouwde autoverhuur partner in Curaçao. Verken het eiland met onze premium voertuigvloot en uitzonderlijke klantenservice.",
      legalNote: "Curacruze is een Nederlandse eenmanszaak (KvK 86655124), geregistreerd in Nederland. We bieden autoverhuur op Curaçao aan als Nederlandse dienstverlener, in samenwerking met lokale partners.",
      termsTitle: "Algemene voorwaarden",
      termsContent: "1. Bedrijfsinformatie\n\nCuracruze is een Nederlandse eenmanszaak, juridisch gevestigd in Nederland en geregistreerd bij de Kamer van Koophandel onder nummer 86655124.\n\nCuracruze biedt autoverhuur op Curaçao aan in samenwerking met lokale verhuurpartners. Curacruze treedt op als contractuele dienstverlener en is het primaire aanspreekpunt voor klanten.\n\n2. Omvang van de dienstverlening\n\nCuracruze biedt online boekingsdiensten voor autoverhuur voor voertuigen op Curaçao. De voertuigen worden lokaal geleverd en beheerd door zorgvuldig geselecteerde externe verhuurpartners.\n\nCuracruze is verantwoordelijk voor:\n• het verwerken van boekingen en betalingen\n• communicatie en klantenservice\n• coördinatie van de huur met lokale partners\n\nDe lokale partner is verantwoordelijk voor:\n• het beschikbaar stellen van het voertuig\n• voertuigconditie en beschikbaarheid\n• lokale aflever- en inleverprocedures\n\n3. Boekingen en betalingen\n\nAlle boekingen worden gemaakt via de Curacruze-website. Alle betalingen worden verwerkt via Curacruze’s Nederlandse onderneming met goedgekeurde betaaldienstverleners.\n\nEen boeking wordt als bevestigd beschouwd zodra de betaling succesvol is afgerond en er een bevestigingsmail is verzonden.\n\n4. Huurovereenkomst\n\nBij het ophalen van het voertuig kan de klant worden gevraagd een lokale huurovereenkomst te ondertekenen bij de lokale partner. Deze overeenkomst kan aanvullende voorwaarden bevatten over gebruik, verzekering, borg en lokale regelgeving.\n\nBij strijdigheid gelden deze Algemene voorwaarden voor de relatie tussen de klant en Curacruze.\n\n5. Bestuurdersvereisten\n\nTenzij anders vermeld, moeten bestuurders:\n• minimaal 21 jaar oud zijn\n• beschikken over een geldig rijbewijs\n• een geldig identiteitsbewijs tonen bij afhalen\n\nAfhankelijk van de lokale partner kunnen aanvullende eisen gelden.\n\n6. Verzekering en aansprakelijkheid\n\nVerzekeringsdekking is onderhevig aan de voorwaarden die tijdens de boeking worden verstrekt en aan het beleid van de lokale partner.\n\nCuracruze is niet aansprakelijk voor:\n• schade door onjuist gebruik van het voertuig\n• overtredingen van lokale verkeersregels\n• boetes, straffen of inbeslagnames\n\nDe klant blijft verantwoordelijk voor het voertuig gedurende de huurperiode.\n\n7. Annuleringen en restituties\n\nAnnulerings- en restitutiebeleid wordt duidelijk vermeld tijdens het boekingsproces.\n\nRestituties, indien van toepassing, worden verwerkt via de oorspronkelijke betaalmethode en afgehandeld door Curacruze’s Nederlandse onderneming.\n\n8. Klachten en support\n\nVragen of klachten dienen rechtstreeks bij Curacruze te worden ingediend via de contactgegevens op de website. Curacruze stemt waar nodig af met de lokale partner.\n\n9. Toepasselijk recht\n\nOp deze Algemene voorwaarden is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland, tenzij dwingend lokaal recht anders bepaalt.\n\n10. Wijzigingen\n\nCuracruze behoudt zich het recht voor deze Algemene voorwaarden op ieder moment te wijzigen. De versie die op de website staat op het moment van boeken is van toepassing.\n\nContactgegevens\nCuracruze\nNederlandse eenmanszaak\nKvK 86655124\nNederland.",
      privacyTitle: "Privacybeleid",
      privacyContent: "1. Inleiding\n\nCuracruze respecteert je privacy en zet zich in om je persoonsgegevens te beschermen. In dit privacybeleid leggen we uit hoe we persoonsgegevens verzamelen, gebruiken, opslaan en beschermen wanneer je onze website bezoekt of onze diensten gebruikt.\n\nCuracruze is een Nederlandse eenmanszaak, juridisch gevestigd in Nederland en geregistreerd bij de Kamer van Koophandel onder nummer 86655124.\n\n2. Persoonsgegevens die wij verzamelen\n\nWij kunnen de volgende persoonsgegevens verzamelen en verwerken:\n• Volledige naam\n• E-mailadres\n• Telefoonnummer\n• Factuur- en betalingsinformatie\n• Boekingsgegevens (huurdata, voertuigvoorkeuren, locatie)\n• Communicatie met de klantenservice\n• IP-adres en browserinformatie (voor beveiliging en eenvoudige analytics)\n\n3. Doeleinden van verwerking\n\nWij verwerken persoonsgegevens voor de volgende doeleinden:\n• Afhandeling van autoverhuurboekingen\n• Verwerking van betalingen en restituties\n• Communicatie met klanten vóór en tijdens de huurperiode\n• Klantenservice\n• Voldoen aan wettelijke en regelgevende verplichtingen\n• Voorkomen van fraude en misbruik van onze diensten\n\n4. Betalingen\n\nBetalingen via onze website worden verwerkt via veilige externe betaaldienstverleners. Curacruze slaat geen volledige betaalkaartgegevens op.\n\nAlle betalingen worden verwerkt via Curacruze’s Nederlandse onderneming.\n\n5. Delen van gegevens\n\nPersoonsgegevens worden alleen gedeeld wanneer dat nodig is:\n• Met lokale verhuurpartners op Curaçao, uitsluitend om de autoverhuur uit te voeren\n• Met betaaldienstverleners voor transactieverwerking\n• Met autoriteiten wanneer dit wettelijk verplicht is\n\nWij verkopen of verhuren geen persoonsgegevens aan derden.\n\n6. Internationale doorgifte\n\nSommige persoonsgegevens kunnen worden gedeeld met servicepartners op Curaçao. In zulke gevallen zorgt Curacruze ervoor dat passende waarborgen aanwezig zijn om persoonsgegevens te beschermen volgens toepasselijke privacywetgeving.\n\n7. Bewaartermijnen\n\nPersoonsgegevens worden niet langer bewaard dan noodzakelijk voor de doeleinden in dit privacybeleid of om te voldoen aan wettelijke verplichtingen.\n\n8. Beveiliging\n\nCuracruze neemt passende technische en organisatorische maatregelen om persoonsgegevens te beschermen tegen ongeoorloofde toegang, verlies of misbruik.\n\n9. Jouw rechten\n\nOnder toepasselijke privacywetgeving heb je het recht om:\n• Inzage te krijgen in je persoonsgegevens\n• Correctie of verwijdering te verzoeken\n• Bezwaar te maken tegen of verwerking te beperken\n• Dataportabiliteit te verzoeken\n\nVerzoeken kunnen worden ingediend via de contactgegevens hieronder.\n\n10. Cookies\n\nCuracruze gebruikt cookies om de website goed te laten functioneren en de gebruikerservaring te verbeteren. Meer informatie over cookies kan in een apart cookiebeleid worden vermeld.\n\n11. Wijzigingen in dit beleid\n\nCuracruze behoudt zich het recht voor dit privacybeleid op ieder moment te wijzigen. De versie die op de website staat op het moment van gebruik is van toepassing.\n\n12. Contactgegevens\n\nCuracruze\nNederlandse eenmanszaak\nKvK 86655124\nNederland\n\nVoor privacygerelateerde vragen kun je contact met ons opnemen via de contactgegevens op de website." 
    },
    // Common
    common: {
      loading: "Laden...",
      error: "Er is iets misgegaan",
      tryAgain: "Probeer Opnieuw",
      close: "Sluiten",
      next: "Volgende",
      previous: "Vorige", 
      select: "Selecteren",
      cancel: "Annuleren",
      confirm: "Bevestigen",
      save: "Opslaan",
      readMore: "Lees meer",
      readLess: "Lees minder"
    }
  },
  es: {
    // Navigation
    // Contact Section
    contact: {
      title: "¿Listo para explorar Curaçao?",
      subtitle: "Ponte en contacto con nuestro amable equipo",
      phone: "Teléfono",
      email: "Email",
      location: "Ubicación",
      hours: "Horario",
      address: "Willemstad, Curaçao",
      legalBusinessAddress: "Dirección legal de la empresa",
      legalBusinessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      businessHours: "Lun-Dom: 8:00 - 20:00",
      cta: "Contáctanos hoy"
    },


    // FAQ Section
    faq: {
      title: "Preguntas Frecuentes",
      subtitle: "Todo lo que necesitas saber sobre alquilar con CuraCruze",
      questions: {
        booking: {
          question: "¿Cómo reservo un coche con CuraCruze?",
          answer: "Simplemente explora nuestra flota, selecciona tu vehículo preferido, elige tus fechas y completa el proceso de pago seguro. Recibirás confirmación inmediata por correo electrónico con instrucciones de recogida."
        },
        requirements: {
          question: "¿Cuáles son los requisitos para alquilar un coche?",
          answer: "Debes tener al menos 21 años y un permiso de conducir válido con un mínimo de 1 año de antigüedad. Los visitantes internacionales necesitan un pasaporte válido y un permiso de conducir internacional."
        },
        payment: {
          question: "¿Qué métodos de pago aceptan?",
          answer: "Aceptamos tarjetas (Visa, Mastercard) e iDEAL (EUR) a través de Stripe Checkout."
        },
        insurance: {
          question: "¿Los coches de CuraCruze vienen con seguro?",
          answer: "Sí, tenemos cobertura de seguro; está incluida con cada alquiler."
        },
        fuel: {
          question: "¿Cuál es su política de combustible?",
          answer: "Todos los coches se entregan con el tanque lleno y deben devolverse con el tanque lleno. Hay una gasolinera convenientemente ubicada a 2 minutos de nuestro punto de recogida."
        },
        pickup: {
          question: "¿Dónde puedo recoger y devolver el coche?",
          answer: "Ofrecemos opciones flexibles de recogida y entrega, incluyendo aeropuertos, centros urbanos e incluso entrega puerta a puerta en áreas seleccionadas. Comprueba la disponibilidad durante la reserva."
        }
      }
    },

    nav: {
      home: "Inicio",
      cars: "Coches",
      calendar: "Calendario",
      about: "Nosotros", 
      contact: "Contacto",
      bookNow: "Reservar",
      language: "Idioma"
    },
    // Hero Section
    hero: {
      title: "¡Paraíso Encontrado!",
      subtitle: "Tu aventura en Curaçao comienza con CuraCruze",
      cta1: "¡Comienza Mi Aventura!",
      cta2: "Descubre el Paraíso",
      description: "Persigue atardeceres, descubre playas escondidas y recorre la isla como un local - tu aventura en Curaçao empieza en cuanto tienes las llaves en la mano.",
      tagline: "Sol, mar y desvíos espontáneos… ¡vamos!",
      vacationBadge: "¡Tu Paraíso Te Espera!",
      driveYourDreams: "Conduce Tus Sueños",
      chips: {
        sun: "Sol",
        beaches: "Playas",
        adventure: "Aventura"
      },
      memoriesWaiting: "Recuerdos Esperando",
      stats: {
        sunnyDays: "Días Soleados/Año",
        beaches: "Playas Prístinas",
        experiences: "Experiencias Únicas",
        satisfied: "Clientes Satisfechos"
      }
    },
    // Cars Section
    cars: {
      sectionTitle: "¡Tu Vacación Comienza Aquí!",
      sectionSubtitle: "¿Listo para hacer realidad tus sueños de Curaçao? ¡Tenemos todo lo que necesitas para la escapada perfecta a la isla!",
      fleetTitle: "Nuestra Flota",
      fleetSubtitle: "Las Llaves De La Libertad",
      viewAll: "Ver Todos los Coches",
      bookNow: "Reservar",
      available: "Disponible",
      unavailable: "No Disponible",
      nextAvailable: "Próximo Disponible",
      seats: "asientos", 
      lowSeason: "Temporada Baja",
      highSeason: "Temporada Alta",
      weeklyFrom: "Semanal desde",
      loading: "Cargando nuestra increíble flota...",
      compact: "Compacto",
      suv: "SUV",
      sedan: "Sedán",
      automatic: "Automático",
      manual: "Manual",
      availability: "Disponibilidad"
    },
    carsPage: {
      bookingInfo: {
        title: "Información de Reserva",
        bookWithUs: "Reserva con Nosotros",
        whatsapp: "WhatsApp",
        email: "Email",
        reservationFee: "Tarifa de reserva: XCG 150 (se deduce del precio total del alquiler)",
        cancellationPolicy: "Política de Cancelación",
        cancel48: "Cancelación gratuita hasta 48 horas antes de la recogida",
        cancel48to24: "Cancelar dentro de 48 h = se pierde el 50% de la tarifa de reserva",
        cancel24: "Cancelar dentro de 24 h = se pierde el 100% de la tarifa de reserva",
        rentalTerms: "Condiciones de Alquiler",
        termNoDeposit: "No se requiere depósito, solo la tarifa de reserva",
        termCleanFullTank: "Coches entregados limpios y con el tanque lleno – devuélvelo igual",
        termInsured: "Todos los vehículos están bien asegurados",
        termPickupDropoff: "Servicio de recogida/entrega disponible",
        termExtras: "Extras como asientos infantiles o conductor adicional bajo solicitud."
      }
    },
    aboutPage: {
      hero: {
        title: "Sobre CuraCruze",
        subtitle: "Tu aventura en Curaçao comienza con CuraCruze"
      },
      intro: {
        title: "CuraCruze Alquiler de Coches",
        description: "Curacruze es una empresa unipersonal neerlandesa (Cámara de Comercio n.º 86655124), legalmente establecida en los Países Bajos. Desde los Países Bajos, ofrecemos servicios de alquiler de coches en Curaçao para viajeros que buscan una experiencia de alquiler fiable y transparente.\n\nEl alquiler de coches en Curaçao se ofrece en cooperación con socios locales cuidadosamente seleccionados. Curacruze actúa como proveedor del servicio y principal punto de contacto para los clientes, mientras que los vehículos son proporcionados localmente por nuestros socios.\n\nTodas las reservas y pagos se procesan a través de nuestra entidad comercial neerlandesa. Garantizamos una comunicación clara, apoyo antes de la llegada y asistencia durante todo el período de alquiler, para que nuestros clientes disfruten de una estancia sin preocupaciones en Curaçao.\n\nSi tienes alguna pregunta, no dudes en ponerte en contacto con nosotros   estaremos encantados de ayudarte."
      },
      why: {
        title: "¿Por qué elegir CuraCruze?",
        reliable: {
          title: "Servicio Confiable",
          description: "Estamos disponibles 24/7. Desde el momento en que aterrizas hasta tu último trayecto al aeropuerto, estamos contigo."
        },
        transparent: {
          title: "Precios Transparentes",
          description: "Sin cargos ocultos. Acuerdos claros y tarifas justas."
        },
        pickup: {
          title: "Recogida y Entrega Gratis",
          description: "Te entregamos el coche de alquiler en tu alojamiento o en el aeropuerto, sin coste adicional."
        },
        vehicles: {
          title: "Vehículos Modernos y Bien Mantenidos",
          description: "Nuestros coches están limpios, bien asegurados y se revisan regularmente para tu tranquilidad."
        },
        local: {
          title: "Local y Cercano",
          description: "Conocemos Curaçao como la palma de la mano y estaremos encantados de compartirte recomendaciones de playas, restaurantes y rincones secretos."
        },
        closing: {
          line1: "Reserva tu coche hoy y explora Curaçao a tu propio ritmo.",
          line2: "¿Tienes preguntas? Contáctanos   estaremos encantados de ayudarte."
        }
      },
      mission: {
        title: "Nuestra Misión",
        description: "En CuraCruze no se trata solo de coches   se trata de la experiencia. Queremos que te sientas libre, seguro y como en casa en la isla. Por eso damos siempre un paso más por ti."
      },
      serve: {
        title: "A Quién Servimos",
        description: "¿Viajas solo, en pareja, en familia o con amigos? Tenemos coches compactos, SUVs espaciosos y todo lo intermedio. ¿Planeas una estancia larga? Pregunta por nuestros descuentos por alquiler extendido."
      },
      cta: {
        title: "¿Listo para explorar Curaçao?",
        subtitle: "¡Contáctanos hoy para reservar tu coche ideal y comenzar tu aventura en la isla!",
        browseFleet: "Ver Nuestra Flota",
        contactUs: "Contactar"
      }
    },
    // Reviews Section
    reviews: {
      title: "Lo Que Dicen Nuestros Clientes",
      subtitle: "Experiencias reales de viajeros felices",
      trustpilot: "Excelente • 4,8/5 • Basado en 200+ reseñas",
      testimonials: {
        thijs: {
          quote: "¡El conocimiento local no tiene precio! El equipo compartió playas secretas y restaurantes increíbles que jamás habríamos encontrado. El coche fue perfecto y el servicio fue de primera clase.",
          name: "Thijs Hendriks",
          role: "Aficionado a la fotografía"
        },
        eva: {
          quote: "Coches limpios, precios justos y un cuidado genuino por los clientes. ¡CuraCruze convirtió nuestras vacaciones en una aventura! Ya estamos planeando nuestro próximo viaje y sin duda volveremos a reservar con ellos.",
          name: "Eva de Jong",
          role: "Cliente recurrente"
        },
        lars: {
          quote: "Servicio profesional de principio a fin. El coche estaba impecable y exactamente como se describía. Excelente relación calidad-precio. ¡Sin duda volveré a usar CuraCruze!",
          name: "Lars van Dam",
          role: "Viajero de negocios"
        },
        anna: {
          quote: "¡Superó nuestras expectativas! El personal nos dio recomendaciones fantásticas de playas escondidas y restaurantes locales. El coche estaba impecable y el servicio fue excelente.",
          name: "Anna Jansen",
          role: "Escapada en pareja"
        },
        larsB: {
          quote: "¡La mejor experiencia de alquiler de coches! El Chevrolet Spark fue perfecto para conducir por la ciudad y aparcar en las playas. La recogida y entrega gratuitas lo hicieron todo muy cómodo.",
          name: "Lars Bakker",
          role: "Amante de la aventura"
        },
        sophie: {
          quote: "¡CuraCruze hizo perfecta nuestra vacaciones en familia! El Kia Rio era espacioso y cómodo. Los asientos infantiles estaban limpios y bien instalados. ¡Servicio excelente en todos los sentidos!",
          name: "Sophie Mulder",
          role: "Vacaciones en familia"
        },
        daan: {
          quote: "Precios transparentes y sin cargos ocultos, tal como prometieron. El proceso de reserva fue muy fluido y el personal fue increíblemente servicial con consejos locales y direcciones.",
          name: "Daan van Leeuwen",
          role: "Primera visita"
        },
        lotte: {
          quote: "¡El soporte 24/7 fue un salvavidas! Tuve un pequeño problema tarde por la noche y lo resolvieron de inmediato. Verdaderos profesionales que se preocupan por sus clientes. ¡Gracias CuraCruze!",
          name: "Lotte Smit",
          role: "Nómada digital"
        }
      }
    },
    common: {
      readMore: "Leer más",
      readLess: "Leer menos"
    },
    carDescriptions: {
      budgetExplorer: "Perfecto para viajeros con presupuesto ajustado. Este Nissan Versa sedán blanco y limpio ofrece comodidad confiable y eficiente en combustible para recorrer Curaçao sin complicaciones. Seguro CDW incluido.",
      islandCruiser: "¡Destaca con este vibrante Kia Soul azul! Ideal para ir de playa en playa y disfrutar de rutas escénicas por Curaçao. Interior amplio y un manejo fácil y divertido. Seguro CDW incluido.",
      coastalComfort: "Elegante Kia Rio sedán blanco, fotografiado junto a la hermosa costa de Curaçao. Cómodo, con estilo y listo para tus aventuras en la isla. Seguro CDW incluido.",
      beachRunner: "¡El compañero perfecto para la playa! Este compacto Chevrolet Spark blanco es ideal para conducir en la ciudad y aparcar fácilmente cerca de las playas más populares de Curaçao. Seguro CDW incluido."
    },
    // Secret Paradise Spots
    paradise: {
      title: "¡Lugares Secretos del Paraíso!",
      subtitle: "Descubre joyas ocultas que solo conocen los locales",
      hiddenBeaches: {
        title: "Playas Ocultas",
        description: "Playas aisladas de arena blanca perfectas para escapadas románticas y relajación pacífica.",
        locations: "Playa Lagun: Cala aislada con tortugas • Playa Jeremi: Pacífica e impresionante • Playa Gipy: El secreto mejor guardado de la isla • Playa Daaibari: Joya tranquila de la costa norte"
      },
      secretSnorkeling: {
        title: "Snorkel Secreto",
        description: "Mundos submarinos prístinos con arrecifes de coral coloridos y peces tropicales.",
        locations: "Playa Piskado: Snorkel con tortugas marinas • Tugboat Beach: Aventura de remolcador hundido • Playa Kalki: Formaciones de coral vibrantes • Playa Santu Pretu: Punto de buceo en naufragio"
      },
      magicalSunsets: {
        title: "Atardeceres Mágicos",
        description: "Miradores de atardecer impresionantes para momentos inolvidables de hora dorada.",
        locations: "Knip Beach: Atardeceres de agua turquesa • Mambo Beach: Ambiente nocturno animado • Shete Boka: Vistas dramáticas de la costa • Klein Curacao: Paraíso de isla deshabitada"
      },
      cta: "¡Alquila un Coche y Explora Estas Joyas Ocultas!",
      tip: "*Pregunta a nuestro equipo por direcciones detalladas y consejos de expertos cuando recojas tu coche"
    },
    // Popular Destinations
    destinations: {
      title: "Destinos Populares",
      subtitle: "Lugares imprescindibles alrededor de la isla",
      pundaOtrobanda: {
        title: "Punda-Otrobanda", 
        name: "Punda-Otrobanda",
        description: "Willemstad histórico con arquitectura colonial holandesa colorida y estatus de Patrimonio Mundial UNESCO."
      },
      janthiel: {
        title: "Playa Janthiel",
        name: "Janthiel",
        description: "Playa prístina con aguas cristalinas, perfecta para nadar y hacer snorkel."
      },
      mambo: {
        title: "Playa Mambo",
        name: "Playa Mambo",
        description: "Escena vibrante de club de playa con restaurantes, bares y actividades de deportes acuáticos."
      }
    },
    // Service Excellence Section
    services: {
      title: "Excelencia de Servicio CuraCruze",
      subtitle: "Todo lo que necesitas para una experiencia perfecta de alquiler de coches en Curazao",
      insurance: {
        title: "Seguro del Coche",
        description: "Cada vehículo de CuraCruze incluye cobertura de seguro completa, para que puedas disfrutar de tu viaje sin preocupaciones."
      },
      pickup: {
        title: "Servicio de Recogida y Entrega",
        description: "En CuraCruze, disfrutarás de servicio gratuito de recogida y entrega, ya sea en el Aeropuerto de Hato o en tu alojamiento. Tu comodidad es nuestra prioridad."
      },
      service: {
        title: "Gran Servicio",
        description: "En CuraCruze, nos enorgullece ofrecer un servicio amable, fiable y profesional, en cada paso del camino. Cuenta con nosotros para un servicio excelente y sin complicaciones de principio a fin."
      },
      reservation: {
        title: "Reserva",
        description: "Requerimos una reserva con al menos 3 días de antelación. El coste de la reserva es XCG 150,-. Este importe se deducirá del precio total."
      },
      cleanCar: {
        title: "Coche Limpio - Tanque Lleno",
        description: "Entregamos el coche limpio y con el tanque lleno. Agradeceríamos mucho que el coche se devuelva en las mismas condiciones   limpio y con el tanque lleno."
      },
      family: {
        title: "Viajar en Familia",
        description: "Proporcionamos sillas infantiles adecuadas para niños de 3 a 12 años. El coste es XCG 5,- por silla y por día."
      }
    },
    // Footer
    footer: {
      quickLinks: "Enlaces Rápidos",
      contact: "Información de Contacto",
      followUs: "Síguenos",
      businessAddress: "Dirección comercial",
      businessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      phoneNumber: "+31 6 51929275",
      aboutUs: "Sobre Nosotros",
      ourFleet: "Nuestra Flota",
      bookingTerms: "Términos de Reserva",
      privacy: "Política de Privacidad",
      pricing: "Precios",
      locations: "Ubicaciones",
      exploreCuracao: "Explorar Curaçao",
      beaches: "Playas",
      divingSpots: "Lugares de Buceo",
      culturalSites: "Sitios Culturales",
      restaurants: "Restaurantes",
      copyright: "© 2024 CuraCruze. Todos los derechos reservados.",
      description: "Tu socio de confianza para alquilar coche en Curaçao. Explora la isla con nuestra flota premium y un servicio al cliente excepcional.",
      legalNote: "Curacruze es una empresa unipersonal neerlandesa (KvK 86655124), registrada en los Países Bajos. Ofrecemos alquiler de coches en Curaçao como proveedor de servicios neerlandés, en colaboración con socios locales.",
      termsTitle: "Términos y condiciones",
      termsContent: "1. Información de la empresa\n\nCuracruze es una empresa unipersonal neerlandesa, legalmente establecida en los Países Bajos y registrada en la Cámara de Comercio neerlandesa con el número 86655124.\n\nCuracruze ofrece servicios de alquiler de coches en Curaçao en cooperación con socios locales. Curacruze actúa como proveedor contractual del servicio y principal punto de contacto para los clientes.\n\n2. Alcance de los servicios\n\nCuracruze ofrece servicios de reserva online de alquiler de coches para vehículos ubicados en Curaçao. Los vehículos son suministrados y gestionados localmente por socios de alquiler externos cuidadosamente seleccionados.\n\nCuracruze es responsable de:\n• procesar reservas y pagos\n• comunicación y soporte al cliente\n• coordinar el alquiler con socios locales\n\nEl socio local es responsable de:\n• proporcionar el vehículo\n• estado y disponibilidad del vehículo\n• procedimientos locales de entrega y devolución\n\n3. Reservas y pagos\n\nTodas las reservas se realizan a través del sitio web de Curacruze. Todos los pagos se procesan a través de la entidad comercial neerlandesa de Curacruze mediante proveedores de pago aprobados.\n\nUna reserva se considera confirmada una vez que el pago se ha completado con éxito y se ha enviado un correo de confirmación.\n\n4. Contrato de alquiler\n\nAl recoger el vehículo, el cliente puede tener que firmar un contrato de alquiler local con el socio local. Este contrato puede incluir condiciones adicionales relacionadas con el uso del vehículo, el seguro, depósitos y normativas locales.\n\nEn caso de conflicto, estos Términos y Condiciones rigen la relación entre el cliente y Curacruze.\n\n5. Requisitos del conductor\n\nSalvo que se indique lo contrario, los conductores deben:\n• tener al menos 21 años\n• poseer un permiso de conducir válido\n• presentar identificación válida en la recogida\n\nPueden aplicarse requisitos adicionales según el socio local.\n\n6. Seguro y responsabilidad\n\nLa cobertura del seguro está sujeta a las condiciones proporcionadas en el momento de la reserva y a la política del socio local.\n\nCuracruze no es responsable de:\n• daños causados por uso indebido del vehículo\n• infracciones de las leyes de tráfico locales\n• multas, sanciones o confiscaciones\n\nEl cliente sigue siendo responsable del vehículo durante el período de alquiler.\n\n7. Cancelaciones y reembolsos\n\nLas políticas de cancelación y reembolso se indican claramente durante el proceso de reserva.\n\nLos reembolsos, si corresponden, se procesan a través del método de pago original y son gestionados por la entidad neerlandesa de Curacruze.\n\n8. Quejas y soporte\n\nLas preguntas o quejas deben enviarse directamente a Curacruze a través de los datos de contacto proporcionados en el sitio web. Curacruze coordinará con el socio local cuando sea necesario.\n\n9. Ley aplicable\n\nEstos Términos y Condiciones se rigen por la ley neerlandesa. Cualquier disputa se presentará ante el tribunal competente de los Países Bajos, salvo que la ley local obligatoria disponga lo contrario.\n\n10. Modificaciones\n\nCuracruze se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Se aplicará la versión publicada en el sitio web en el momento de la reserva.\n\nInformación de contacto\nCuracruze\nEmpresa unipersonal neerlandesa\nCámara de Comercio n.º 86655124\nPaíses Bajos.",
      privacyTitle: "Política de privacidad",
      privacyContent: "1. Introducción\n\nCuracruze respeta tu privacidad y se compromete a proteger tus datos personales. Esta Política de privacidad explica cómo recopilamos, usamos, almacenamos y protegemos la información personal cuando visitas nuestro sitio web o utilizas nuestros servicios.\n\nCuracruze es una empresa unipersonal neerlandesa, legalmente establecida en los Países Bajos y registrada en la Cámara de Comercio neerlandesa con el número 86655124.\n\n2. Datos personales que recopilamos\n\nPodemos recopilar y procesar los siguientes datos personales:\n• Nombre completo\n• Dirección de correo electrónico\n• Número de teléfono\n• Información de facturación y pago\n• Detalles de la reserva (fechas de alquiler, preferencias de vehículo, ubicación)\n• Comunicación con atención al cliente\n• Dirección IP e información del navegador (para seguridad y analítica básica)\n\n3. Finalidad del tratamiento\n\nTratamos los datos personales con los siguientes fines:\n• Gestionar reservas de alquiler de coches\n• Procesar pagos y reembolsos\n• Comunicarnos con los clientes antes y durante el período de alquiler\n• Proporcionar atención al cliente\n• Cumplir obligaciones legales y regulatorias\n• Prevenir fraude y uso indebido de nuestros servicios\n\n4. Pagos\n\nLos pagos realizados a través de nuestro sitio web se procesan mediante proveedores de pago externos seguros. Curacruze no almacena los datos completos de la tarjeta.\n\nTodos los pagos se procesan a través de la entidad neerlandesa de Curacruze.\n\n5. Compartición de datos\n\nLos datos personales solo se comparten cuando es necesario:\n• Con socios locales de alquiler en Curaçao, únicamente para ejecutar el alquiler\n• Con proveedores de pago para el procesamiento de transacciones\n• Con autoridades cuando sea legalmente requerido\n\nNo vendemos ni alquilamos datos personales a terceros.\n\n6. Transferencias internacionales\n\nAlgunos datos personales pueden compartirse con socios ubicados en Curaçao. En esos casos, Curacruze garantiza salvaguardas adecuadas para proteger los datos de acuerdo con la normativa aplicable.\n\n7. Conservación\n\nLos datos personales se conservan solo durante el tiempo necesario para cumplir los fines descritos en esta Política o para cumplir obligaciones legales.\n\n8. Seguridad\n\nCuracruze aplica medidas técnicas y organizativas adecuadas para proteger los datos contra accesos no autorizados, pérdida o uso indebido.\n\n9. Tus derechos\n\nSegún la normativa aplicable, tienes derecho a:\n• Acceder a tus datos personales\n• Solicitar corrección o eliminación\n• Oponerte o restringir el tratamiento\n• Solicitar portabilidad\n\nLas solicitudes pueden enviarse usando los datos de contacto indicados abajo.\n\n10. Cookies\n\nCuracruze utiliza cookies para garantizar el correcto funcionamiento del sitio y mejorar la experiencia. Puede proporcionarse información detallada en una Política de cookies separada.\n\n11. Cambios en esta política\n\nCuracruze se reserva el derecho de actualizar esta Política de privacidad en cualquier momento. Se aplicará la versión publicada en el sitio en el momento de uso.\n\n12. Información de contacto\n\nCuracruze\nEmpresa unipersonal neerlandesa\nCámara de Comercio n.º 86655124\nPaíses Bajos\n\nPara preguntas relacionadas con privacidad, contáctanos a través de los datos de contacto proporcionados en el sitio web."
    }
  },
  de: {
    // Navigation
    // Contact Section
    contact: {
      title: "Bereit, Curaçao zu entdecken?",
      subtitle: "Kontaktieren Sie unser freundliches Team",
      phone: "Telefon",
      email: "E-Mail",
      location: "Standort",
      hours: "Geschäftszeiten",
      address: "Willemstad, Curaçao",
      legalBusinessAddress: "Rechtliche Geschäftsadresse",
      legalBusinessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      businessHours: "Mo-So: 8:00 - 20:00",
      cta: "Kontaktieren Sie uns heute"
    },

    // Footer
    footer: {
      quickLinks: "Schnelllinks",
      contact: "Kontakt Info",
      followUs: "Folgen Sie Uns",
      businessAddress: "Geschäftsadresse",
      businessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      phoneNumber: "+31 6 51929275",
      aboutUs: "Über Uns",
      ourFleet: "Unsere Flotte",
      bookingTerms: "Buchungsbedingungen",
      privacy: "Datenschutz",
      pricing: "Preise",
      locations: "Standorte",
      exploreCuracao: "Curaçao Entdecken",
      beaches: "Strände",
      divingSpots: "Tauchspots",
      culturalSites: "Kulturelle Orte",
      restaurants: "Restaurants",
      copyright: "© 2024 CuraCruze. Alle Rechte vorbehalten.",
      description: "Ihr vertrauensvoller Mietwagenpartner auf Curaçao. Erkunden Sie die Insel mit unserer Premium-Flotte und außergewöhnlichem Kundenservice.",
      legalNote: "Curacruze ist ein niederländisches Einzelunternehmen (KvK 86655124), in den Niederlanden registriert. Wir bieten Mietwagen auf Curaçao als niederländischer Dienstleister an, in Zusammenarbeit mit lokalen Partnern.",
      termsTitle: "Allgemeine Geschäftsbedingungen",
      termsContent: "1. Unternehmensinformationen\n\nCuracruze ist ein niederländisches Einzelunternehmen, rechtlich in den Niederlanden gegründet und bei der niederländischen Handelskammer unter der Nummer 86655124 registriert.\n\nCuracruze bietet Mietwagenservices auf Curaçao in Zusammenarbeit mit lokalen Vermietpartnern an. Curacruze agiert als vertraglicher Dienstleister und primärer Ansprechpartner für Kunden.\n\n2. Leistungsumfang\n\nCuracruze bietet Online-Buchungsdienste für Mietwagen für Fahrzeuge auf Curaçao an. Die Fahrzeuge werden lokal von sorgfältig ausgewählten Drittanbieter-Partnern bereitgestellt und betrieben.\n\nCuracruze ist verantwortlich für:\n• Abwicklung von Buchungen und Zahlungen\n• Kundenkommunikation und Support\n• Koordination der Anmietung mit lokalen Partnern\n\nDer lokale Partner ist verantwortlich für:\n• Bereitstellung des Fahrzeugs\n• Zustand und Verfügbarkeit des Fahrzeugs\n• lokale Übergabe- und Rückgabeprozesse\n\n3. Buchungen und Zahlungen\n\nAlle Buchungen erfolgen über die Curacruze-Website. Alle Zahlungen werden über Curacruze’s niederländische Geschäftseinheit mit zugelassenen Zahlungsdienstleistern verarbeitet.\n\nEine Buchung gilt als bestätigt, sobald die Zahlung erfolgreich abgeschlossen und eine Bestätigungs-E-Mail versendet wurde.\n\n4. Mietvertrag\n\nBei der Fahrzeugabholung kann der Kunde aufgefordert werden, einen lokalen Mietvertrag beim lokalen Partner zu unterzeichnen. Dieser Vertrag kann zusätzliche Bedingungen zu Fahrzeugnutzung, Versicherung, Kaution und lokalen Vorschriften enthalten.\n\nIm Konfliktfall gelten diese Allgemeinen Geschäftsbedingungen für die Beziehung zwischen dem Kunden und Curacruze.\n\n5. Anforderungen an den Fahrer\n\nSofern nicht anders angegeben, müssen Fahrer:\n• mindestens 21 Jahre alt sein\n• einen gültigen Führerschein besitzen\n• bei Abholung einen gültigen Ausweis vorlegen\n\nJe nach lokalem Partner können zusätzliche Anforderungen gelten.\n\n6. Versicherung und Haftung\n\nDer Versicherungsschutz unterliegt den bei der Buchung bereitgestellten Bedingungen sowie der Police des lokalen Partners.\n\nCuracruze haftet nicht für:\n• Schäden durch missbräuchliche Nutzung des Fahrzeugs\n• Verstöße gegen lokale Verkehrsregeln\n• Bußgelder, Strafen oder Beschlagnahmungen\n\nDer Kunde bleibt während der Mietdauer für das Fahrzeug verantwortlich.\n\n7. Stornierungen und Erstattungen\n\nStornierungs- und Erstattungsrichtlinien werden während des Buchungsvorgangs klar angegeben.\n\nErstattungen, sofern zutreffend, werden über die ursprüngliche Zahlungsmethode abgewickelt und von Curacruze’s niederländischer Geschäftseinheit bearbeitet.\n\n8. Beschwerden und Support\n\nFragen oder Beschwerden sind direkt an Curacruze über die auf der Website angegebenen Kontaktdaten zu richten. Curacruze koordiniert bei Bedarf mit dem lokalen Partner.\n\n9. Anwendbares Recht\n\nDiese Allgemeinen Geschäftsbedingungen unterliegen niederländischem Recht. Streitigkeiten werden dem zuständigen Gericht in den Niederlanden vorgelegt, sofern zwingendes lokales Recht nichts anderes vorsieht.\n\n10. Änderungen\n\nCuracruze behält sich das Recht vor, diese Allgemeinen Geschäftsbedingungen jederzeit zu ändern. Es gilt die zum Zeitpunkt der Buchung auf der Website veröffentlichte Version.\n\nKontaktinformationen\nCuracruze\nNiederländisches Einzelunternehmen\nHandelskammer-Nr. 86655124\nNiederlande.",
      privacyTitle: "Datenschutzerklärung",
      privacyContent: "1. Einleitung\n\nCuracruze respektiert Ihre Privatsphäre und setzt sich für den Schutz Ihrer personenbezogenen Daten ein. Diese Datenschutzerklärung erläutert, wie wir personenbezogene Daten erheben, verwenden, speichern und schützen, wenn Sie unsere Website besuchen oder unsere Dienste nutzen.\n\nCuracruze ist ein niederländisches Einzelunternehmen, rechtlich in den Niederlanden gegründet und bei der niederländischen Handelskammer unter der Nummer 86655124 registriert.\n\n2. Welche Daten wir erheben\n\nWir können folgende personenbezogene Daten erheben und verarbeiten:\n• Vollständiger Name\n• E-Mail-Adresse\n• Telefonnummer\n• Rechnungs- und Zahlungsinformationen\n• Buchungsdetails (Mietdaten, Fahrzeugwünsche, Ort)\n• Kommunikation mit dem Kundensupport\n• IP-Adresse und Browserinformationen (für Sicherheit und einfache Analysen)\n\n3. Zwecke der Verarbeitung\n\nWir verarbeiten personenbezogene Daten zu folgenden Zwecken:\n• Abwicklung von Mietwagenbuchungen\n• Verarbeitung von Zahlungen und Erstattungen\n• Kommunikation mit Kunden vor und während der Mietdauer\n• Kundensupport\n• Erfüllung gesetzlicher und regulatorischer Pflichten\n• Verhinderung von Betrug und Missbrauch unserer Dienste\n\n4. Zahlungen\n\nZahlungen über unsere Website werden über sichere Drittanbieter-Zahlungsdienstleister verarbeitet. Curacruze speichert keine vollständigen Kartendaten.\n\nAlle Zahlungen werden über Curacruze’s niederländische Geschäftseinheit verarbeitet.\n\n5. Weitergabe von Daten\n\nPersonenbezogene Daten werden nur bei Bedarf weitergegeben:\n• An lokale Vermietpartner auf Curaçao, ausschließlich zur Durchführung der Anmietung\n• An Zahlungsdienstleister zur Transaktionsabwicklung\n• An Behörden, wenn dies gesetzlich erforderlich ist\n\nWir verkaufen oder vermieten keine personenbezogenen Daten an Dritte.\n\n6. Internationale Datenübermittlungen\n\nEinige personenbezogene Daten können mit Servicepartnern auf Curaçao geteilt werden. In solchen Fällen stellt Curacruze sicher, dass angemessene Schutzmaßnahmen gemäß den geltenden Datenschutzgesetzen getroffen werden.\n\n7. Aufbewahrung\n\nPersonenbezogene Daten werden nur so lange aufbewahrt, wie es zur Erfüllung der in dieser Datenschutzerklärung beschriebenen Zwecke oder zur Einhaltung gesetzlicher Pflichten erforderlich ist.\n\n8. Datensicherheit\n\nCuracruze trifft geeignete technische und organisatorische Maßnahmen, um personenbezogene Daten vor unbefugtem Zugriff, Verlust oder Missbrauch zu schützen.\n\n9. Ihre Rechte\n\nNach geltendem Datenschutzrecht haben Sie das Recht auf:\n• Auskunft über Ihre personenbezogenen Daten\n• Berichtigung oder Löschung\n• Widerspruch oder Einschränkung der Verarbeitung\n• Datenübertragbarkeit\n\nAnfragen können über die unten angegebenen Kontaktdaten gestellt werden.\n\n10. Cookies\n\nCuracruze verwendet Cookies, um die ordnungsgemäße Funktion der Website sicherzustellen und die Nutzererfahrung zu verbessern. Detaillierte Informationen können in einer separaten Cookie-Richtlinie bereitgestellt werden.\n\n11. Änderungen dieser Richtlinie\n\nCuracruze behält sich das Recht vor, diese Datenschutzerklärung jederzeit zu aktualisieren. Es gilt die zum Zeitpunkt der Nutzung auf der Website veröffentlichte Version.\n\n12. Kontaktinformationen\n\nCuracruze\nNiederländisches Einzelunternehmen\nHandelskammer-Nr. 86655124\nNiederlande\n\nFür datenschutzbezogene Fragen kontaktieren Sie uns bitte über die auf der Website angegebenen Kontaktdaten." 
    },

    // FAQ Section
    faq: {
      title: "Häufig Gestellte Fragen",
      subtitle: "Alles, was Sie über das Mieten bei CuraCruze wissen müssen",
      questions: {
        booking: {
          question: "Wie buche ich ein Auto bei CuraCruze?",
          answer: "Stöbern Sie einfach in unserer Flotte, wählen Sie Ihr bevorzugtes Fahrzeug, wählen Sie Ihre Daten und schließen Sie den sicheren Zahlungsvorgang ab. Sie erhalten sofort eine Bestätigung per E-Mail mit Abholhinweisen."
        },
        requirements: {
          question: "Welche Voraussetzungen gelten, um ein Auto zu mieten?",
          answer: "Sie müssen mindestens 21 Jahre alt sein und einen gültigen Führerschein besitzen, der seit mindestens 1 Jahr besteht. Internationale Besucher benötigen einen gültigen Reisepass und einen internationalen Führerschein."
        },
        payment: {
          question: "Welche Zahlungsmethoden akzeptieren Sie?",
          answer: "Wir akzeptieren Kreditkarten (Visa, Mastercard) und iDEAL (EUR) über Stripe Checkout."
        },
        insurance: {
          question: "Haben CuraCruze-Autos eine Versicherung?",
          answer: "Ja, wir haben Versicherungsschutz; er ist bei jeder Miete inklusive."
        },
        fuel: {
          question: "Wie lautet Ihre Kraftstoffregelung?",
          answer: "Alle Autos werden mit vollem Tank bereitgestellt und sollten mit vollem Tank zurückgegeben werden. Eine Tankstelle befindet sich bequem nur 2 Minuten von unserem Abholort entfernt."
        },
        pickup: {
          question: "Wo kann ich das Auto abholen und zurückgeben?",
          answer: "Wir bieten flexible Abhol- und Rückgabeoptionen, darunter Flughäfen, Stadtknotenpunkte und in ausgewählten Gebieten sogar eine Tür-zu-Tür-Lieferung. Prüfen Sie die Verfügbarkeit während der Buchung."
        }
      }
    },

    nav: {
      home: "Start",
      cars: "Autos",
      calendar: "Kalender",
      about: "Über Uns",
      contact: "Kontakt",
      bookNow: "Jetzt Buchen",
      language: "Sprache"
    },
    // Hero Section
    hero: {
      title: "Paradies Gefunden!",
      subtitle: "Ihr Curaçao-Abenteuer beginnt mit CuraCruze",
      cta1: "Mein Abenteuer Starten!",
      cta2: "Paradies Entdecken",
      description: "Jagen Sie Sonnenuntergänge, entdecken Sie versteckte Strände und cruisen Sie die Insel wie ein Local - Ihr Curaçao-Abenteuer beginnt, sobald Sie die Schlüssel in der Hand haben.",
      tagline: "Sonne, Meer und spontane Umwege… los geht's!",
      vacationBadge: "Ihr Paradies Wartet!",
      driveYourDreams: "Fahren Sie Ihre Träume",
      chips: {
        sun: "Sonne",
        beaches: "Strände",
        adventure: "Abenteuer"
      },
      memoriesWaiting: "Erinnerungen Warten",
      stats: {
        sunnyDays: "Sonnige Tage/Jahr",
        beaches: "Unberührte Strände", 
        experiences: "Einzigartige Erlebnisse",
        satisfied: "Zufriedene Kunden"
      }
    },
    // Cars Section
    cars: {
      sectionTitle: "Ihr Urlaub Beginnt Hier!",
      sectionSubtitle: "Bereit, Ihre Curaçao-Träume wahr werden zu lassen? Wir haben alles, was Sie für den perfekten Inselurlaub brauchen!",
      fleetTitle: "Unsere Flotte",
      fleetSubtitle: "Die Schlüssel Zur Freiheit",
      viewAll: "Alle Autos Ansehen",
      bookNow: "Jetzt Buchen",
      available: "Verfügbar",
      unavailable: "Nicht Verfügbar",
      nextAvailable: "Nächste Verfügbar",
      seats: "Sitze",
      lowSeason: "Nebensaison", 
      highSeason: "Hauptsaison",
      weeklyFrom: "Wöchentlich ab",
      loading: "Unsere großartige Flotte wird geladen...",
      compact: "Kompakt",
      suv: "SUV",
      sedan: "Limousine",
      automatic: "Automatik",
      manual: "Manuell",
      availability: "Verfügbarkeit"
    },
    carsPage: {
      bookingInfo: {
        title: "Buchungsinformationen",
        bookWithUs: "Buchen Sie Bei Uns",
        whatsapp: "WhatsApp",
        email: "E-Mail",
        reservationFee: "Reservierungsgebühr: XCG 150 (wird vom Gesamtmietpreis abgezogen)",
        cancellationPolicy: "Stornierungsbedingungen",
        cancel48: "Kostenlose Stornierung bis 48 Stunden vor Abholung",
        cancel48to24: "Storno innerhalb von 48 Std. = 50% der Reservierungsgebühr verfällt",
        cancel24: "Storno innerhalb von 24 Std. = 100% der Reservierungsgebühr verfällt",
        rentalTerms: "Mietbedingungen",
        termNoDeposit: "Keine Kaution erforderlich, nur die Reservierungsgebühr",
        termCleanFullTank: "Fahrzeuge werden sauber und mit vollem Tank geliefert – bitte genauso zurückgeben",
        termInsured: "Alle Fahrzeuge sind gut versichert",
        termPickupDropoff: "Abhol-/Rückgabe-Service verfügbar",
        termExtras: "Extras wie Kindersitze oder zusätzlicher Fahrer auf Anfrage."
      }
    },
    aboutPage: {
      hero: {
        title: "Über CuraCruze",
        subtitle: "Ihr Curaçao-Abenteuer beginnt mit CuraCruze"
      },
      intro: {
        title: "CuraCruze Autovermietung",
        description: "Curacruze ist ein niederländisches Einzelunternehmen (Handelskammer-Nr. 86655124), rechtlich in den Niederlanden gegründet. Von den Niederlanden aus bieten wir Mietwagenservices auf Curaçao für Reisende an, die ein zuverlässiges und transparentes Mieterlebnis suchen.\n\nMietwagen auf Curaçao werden in Zusammenarbeit mit sorgfältig ausgewählten lokalen Partnern angeboten. Curacruze fungiert als Dienstleister und primärer Ansprechpartner für Kunden, während die Fahrzeuge lokal durch unsere Partner bereitgestellt werden.\n\nAlle Buchungen und Zahlungen werden über unsere niederländische Geschäftseinheit abgewickelt. Wir sorgen für klare Kommunikation, Unterstützung vor der Anreise und Hilfe während der gesamten Mietdauer, damit unsere Kunden Curaçao sorgenfrei genießen können.\n\nWenn Sie Fragen haben, kontaktieren Sie uns gerne   wir helfen Ihnen jederzeit weiter."
      },
      why: {
        title: "Warum CuraCruze?",
        reliable: {
          title: "Zuverlässiger Service",
          description: "Wir sind rund um die Uhr erreichbar. Vom Moment Ihrer Ankunft bis zur letzten Fahrt zum Flughafen sind wir für Sie da."
        },
        transparent: {
          title: "Transparente Preise",
          description: "Keine versteckten Gebühren. Klare Absprachen und faire Tarife."
        },
        pickup: {
          title: "Kostenloser Abhol- und Rückgabe-Service",
          description: "Wir bringen Ihnen den Mietwagen kostenlos zu Ihrer Unterkunft oder zum Flughafen."
        },
        vehicles: {
          title: "Moderne, Gut Gewartete Fahrzeuge",
          description: "Unsere Fahrzeuge sind sauber, gut versichert und werden regelmäßig gewartet – für Ihre Sicherheit und Ihr gutes Gefühl."
        },
        local: {
          title: "Lokal und Persönlich",
          description: "Wir kennen Curaçao wie unsere Westentasche und teilen gern Tipps zu den besten Stränden, Restaurants und versteckten Highlights."
        },
        closing: {
          line1: "Buchen Sie Ihren Mietwagen noch heute und entdecken Sie Curaçao in Ihrem eigenen Tempo.",
          line2: "Fragen? Melden Sie sich gerne   wir helfen Ihnen jederzeit weiter!"
        }
      },
      mission: {
        title: "Unsere Mission",
        description: "Bei CuraCruze geht es nicht nur um Autos   es geht um das Erlebnis. Wir möchten, dass Sie sich frei, sicher und wie zu Hause auf der Insel fühlen. Deshalb gehen wir für Sie die Extra-Meile."
      },
      serve: {
        title: "Für Wen Wir Da Sind",
        description: "Allein, zu zweit, mit der Familie oder mit Freunden unterwegs? Wir haben kompakte Autos, geräumige SUVs und alles dazwischen. Planen Sie einen längeren Aufenthalt? Fragen Sie nach unseren Rabatten für Langzeitmieten."
      },
      cta: {
        title: "Bereit, Curaçao zu entdecken?",
        subtitle: "Kontaktieren Sie uns noch heute, buchen Sie Ihr perfektes Mietauto und starten Sie Ihr Inselabenteuer!",
        browseFleet: "Unsere Flotte ansehen",
        contactUs: "Kontakt"
      }
    },
    // Reviews Section
    reviews: {
      title: "Was Unsere Kunden Sagen",
      subtitle: "Echte Erfahrungen von zufriedenen Reisenden",
      trustpilot: "Ausgezeichnet • 4,8/5 • Basierend auf 200+ Bewertungen",
      testimonials: {
        thijs: {
          quote: "Lokales Wissen ist unbezahlbar! Das Team teilte geheime Strände und großartige Restaurants, die wir sonst nie gefunden hätten. Das Auto war perfekt und der Service war Weltklasse.",
          name: "Thijs Hendriks",
          role: "Fotografie-Fan"
        },
        eva: {
          quote: "Saubere Autos, faire Preise und echte Fürsorge für Kunden. CuraCruze machte unseren Urlaub zu einem Abenteuer! Wir planen bereits die nächste Reise und werden definitiv wieder bei ihnen buchen.",
          name: "Eva de Jong",
          role: "Stammkundin"
        },
        lars: {
          quote: "Professioneller Service von Anfang bis Ende. Das Auto war makellos und genau wie beschrieben. Sehr gutes Preis-Leistungs-Verhältnis. Ich werde CuraCruze definitiv wieder nutzen!",
          name: "Lars van Dam",
          role: "Geschäftsreisender"
        },
        anna: {
          quote: "Unsere Erwartungen wurden übertroffen! Das Team gab uns fantastische Empfehlungen für versteckte Strände und lokale Restaurants. Das Auto war blitzsauber und der Service hervorragend.",
          name: "Anna Jansen",
          role: "Paarurlaub"
        },
        larsB: {
          quote: "Beste Mietwagen-Erfahrung überhaupt! Der Chevrolet Spark war perfekt für Stadtfahrten und das Parken an den Stränden. Kostenlose Abholung und Rückgabe machten alles sehr bequem.",
          name: "Lars Bakker",
          role: "Abenteuerlustiger"
        },
        sophie: {
          quote: "CuraCruze machte unseren Familienurlaub perfekt! Der Kia Rio war geräumig und komfortabel. Die Kindersitze waren sauber und korrekt installiert. Rundum hervorragender Service!",
          name: "Sophie Mulder",
          role: "Familienurlaub"
        },
        daan: {
          quote: "Transparente Preise und keine versteckten Gebühren – genau wie versprochen! Der Buchungsprozess war reibungslos und das Team war unglaublich hilfreich mit lokalen Tipps und Wegbeschreibungen.",
          name: "Daan van Leeuwen",
          role: "Erstbesucher"
        },
        lotte: {
          quote: "Der 24/7-Support war ein Lebensretter! Ich hatte spät in der Nacht ein kleines Problem und sie haben es sofort gelöst. Echte Profis, denen ihre Kunden wichtig sind. Danke CuraCruze!",
          name: "Lotte Smit",
          role: "Digital Nomad"
        }
      }
    },
    common: {
      readMore: "Mehr lesen",
      readLess: "Weniger anzeigen"
    },
    carDescriptions: {
      budgetExplorer: "Perfekt für preisbewusste Reisende. Diese saubere weiße Nissan Versa Limousine bietet zuverlässigen, sparsamen Komfort, um ganz Curaçao entspannt zu erkunden. CDW-Versicherung inklusive.",
      islandCruiser: "Fallen Sie auf mit diesem leuchtend blauen Kia Soul – ideal für Strand-Hopping und malerische Fahrten über Curaçao. Geräumiger Innenraum und ein angenehm leichtes Fahrgefühl. CDW-Versicherung inklusive.",
      coastalComfort: "Elegante weiße Kia Rio Limousine, fotografiert an Curaçaos wunderschöner Küste. Komfortabel, stilvoll und bereit für jedes Inselabenteuer. CDW-Versicherung inklusive.",
      beachRunner: "Der ultimative Strandbegleiter! Dieser kompakte weiße Chevrolet Spark ist perfekt für Stadtfahrten und unkompliziertes Parken in der Nähe von Curaçaos beliebtesten Stränden. CDW-Versicherung inklusive."
    },
    // Secret Paradise Spots
    paradise: {
      title: "Geheime Paradies-Orte!",
      subtitle: "Entdecken Sie verborgene Schätze, die nur Einheimische kennen",
      hiddenBeaches: {
        title: "Versteckte Strände",
        description: "Abgeschiedene weiße Sandstrände perfekt für romantische Ausflüge und friedliche Entspannung.",
        locations: "Playa Lagun: Abgeschiedene Bucht mit Schildkröten • Playa Jeremi: Friedlich und atemberaubend • Playa Gipy: Das bestgehütete Geheimnis der Insel • Playa Daaibari: Ruhiges Juwel der Nordküste"
      },
      secretSnorkeling: {
        title: "Geheimes Schnorcheln",
        description: "Unberührte Unterwasserwelten mit farbenfrohen Korallenriffen und tropischen Fischen.",
        locations: "Playa Piskado: Schnorcheln mit Meeresschildkröten • Tugboat Beach: Versunkenes Schlepper-Abenteuer • Playa Kalki: Lebendige Korallenformationen • Playa Santu Pretu: Schiffswrack-Tauchplatz"
      },
      magicalSunsets: {
        title: "Magische Sonnenuntergänge",
        description: "Atemberaubende Sonnenuntergang-Aussichtspunkte für unvergessliche goldene Stunden-Momente.",
        locations: "Knip Beach: Türkise Wasser-Sonnenuntergänge • Mambo Beach: Lebhafte Abend-Atmosphäre • Shete Boka: Dramatische Küstenlinie-Ausblicke • Klein Curacao: Unbewohntes Insel-Paradies"
      },
      cta: "Mieten Sie ein Auto & Entdecken Sie Diese Verborgenen Schätze!",
      tip: "*Fragen Sie unser Team nach detaillierten Wegbeschreibungen und Insider-Tipps wenn Sie Ihr Auto abholen"
    },
    // Popular Destinations
    destinations: {
      title: "Beliebte Reiseziele",
      subtitle: "Sehenswerte Orte rund um die Insel",
      pundaOtrobanda: {
        title: "Punda-Otrobanda",
        name: "Punda-Otrobanda", 
        description: "Historisches Willemstad mit farbenfroher niederländischer Kolonialarchitektur und UNESCO-Weltkulturerbe-Status."
      },
      janthiel: {
        title: "Janthiel Strand",
        name: "Janthiel",
        description: "Unberührter Strand mit kristallklarem Wasser, perfekt zum Schwimmen und Schnorcheln."
      },
      mambo: {
        title: "Mambo Strand",
        name: "Mambo Strand",
        description: "Lebendige Strandclub-Szene mit Restaurants, Bars und Wassersportaktivitäten."
      }
    },
    // Service Excellence Section
    services: {
      title: "CuraCruze Service-Exzellenz",
      subtitle: "Alles, was Sie für ein perfektes Mietwagen-Erlebnis auf Curaçao brauchen",
      insurance: {
        title: "Kfz-Versicherung",
        description: "Jedes CuraCruze-Fahrzeug hat eine umfassende Versicherung inklusive, damit Sie Ihre Reise sorgenfrei genießen können."
      },
      pickup: {
        title: "Abhol- & Rückgabe-Service",
        description: "Bei CuraCruze genießen Sie einen kostenlosen Abhol- und Rückgabe-Service – ob am Flughafen Hato oder an Ihrer Unterkunft. Ihr Komfort hat für uns Priorität."
      },
      service: {
        title: "Top-Service",
        description: "Bei CuraCruze sind wir stolz darauf, freundlichen, zuverlässigen und professionellen Service zu bieten – bei jedem Schritt. Verlassen Sie sich auf hervorragenden, stressfreien Service von Anfang bis Ende."
      },
      reservation: {
        title: "Reservierung",
        description: "Wir benötigen eine Reservierung mindestens 3 Tage im Voraus. Die Kosten der Reservierung betragen XCG 150,-. Dieser Betrag wird vom Gesamtpreis abgezogen."
      },
      cleanCar: {
        title: "Sauberes Auto – Voller Tank",
        description: "Wir übergeben das Auto sauber und mit vollem Tank. Wir würden es sehr schätzen, wenn das Auto im gleichen Zustand zurückgegeben wird   sauber und mit vollem Tank."
      },
      family: {
        title: "Reisen mit der Familie",
        description: "Wir stellen Kindersitze für Kinder im Alter von 3 bis 12 Jahren zur Verfügung. Die Kosten betragen XCG 5,- pro Sitz und Tag."
      }
    }
  },
  fr: {
    // Navigation
    // Contact Section
    contact: {
      title: "Prêt à explorer Curaçao ?",
      subtitle: "Contactez notre équipe sympathique",
      phone: "Téléphone",
      email: "Email",
      location: "Lieu",
      hours: "Heures d'ouverture",
      address: "Willemstad, Curaçao",
      legalBusinessAddress: "Adresse légale de l’entreprise",
      legalBusinessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      businessHours: "Lun-Dim: 8:00 - 20:00",
      cta: "Contactez-nous aujourd'hui"
    },


    // Footer
    footer: {
      quickLinks: "Liens Rapides",
      contact: "Contact Info",
      followUs: "Suivez-nous",
      businessAddress: "Adresse commerciale",
      businessAddressLine: "Haringvliet 505, 3011ZP Rotterdam (no visiting address)",
      phoneNumber: "+31 6 51929275",
      aboutUs: "À Propos",
      ourFleet: "Notre Flotte",
      bookingTerms: "Conditions de Réservation",
      privacy: "Politique de Confidentialité",
      pricing: "Tarifs",
      locations: "Lieux",
      exploreCuracao: "Explorer Curaçao",
      beaches: "Plages",
      divingSpots: "Sites de Plongée",
      culturalSites: "Sites Culturels",
      restaurants: "Restaurants",
      copyright: "© 2024 CuraCruze. Tous droits réservés.",
      description: "Votre partenaire de location de voiture de confiance à Curaçao. Explorez l'île avec notre flotte premium et notre service client exceptionnel.",
      legalNote: "Curacruze est une entreprise individuelle néerlandaise (KvK 86655124), enregistrée aux Pays-Bas. Nous proposons la location de voitures à Curaçao en tant que prestataire de services néerlandais, en collaboration avec des partenaires locaux.",
      termsTitle: "Conditions générales",
      termsContent: "1. Informations sur l’entreprise\n\nCuracruze est une entreprise individuelle néerlandaise, légalement établie aux Pays-Bas et enregistrée auprès de la Chambre de commerce néerlandaise sous le numéro 86655124.\n\nCuracruze propose des services de location de voitures à Curaçao en coopération avec des partenaires locaux. Curacruze agit en tant que prestataire contractuel du service et principal point de contact pour les clients.\n\n2. Portée des services\n\nCuracruze fournit des services de réservation en ligne pour des véhicules situés à Curaçao. Les véhicules sont fournis et exploités localement par des partenaires tiers soigneusement sélectionnés.\n\nCuracruze est responsable de :\n• traiter les réservations et les paiements\n• la communication et l’assistance client\n• coordonner la location avec les partenaires locaux\n\nLe partenaire local est responsable de :\n• fournir le véhicule\n• l’état et la disponibilité du véhicule\n• les procédures locales de livraison et de retour\n\n3. Réservations et paiements\n\nToutes les réservations sont effectuées via le site web Curacruze. Tous les paiements sont traités via l’entité commerciale néerlandaise de Curacruze avec des prestataires de paiement approuvés.\n\nUne réservation est considérée comme confirmée une fois le paiement effectué avec succès et l’e-mail de confirmation envoyé.\n\n4. Contrat de location\n\nLors de la prise en charge du véhicule, le client peut être tenu de signer un contrat de location local avec le partenaire local. Ce contrat peut inclure des conditions supplémentaires concernant l’usage du véhicule, l’assurance, les dépôts et la réglementation locale.\n\nEn cas de conflit, les présentes Conditions générales régissent la relation entre le client et Curacruze.\n\n5. Conditions du conducteur\n\nSauf indication contraire, les conducteurs doivent :\n• avoir au moins 21 ans\n• détenir un permis de conduire valide\n• présenter une pièce d’identité valide lors de la prise en charge\n\nDes exigences supplémentaires peuvent s’appliquer selon le partenaire local.\n\n6. Assurance et responsabilité\n\nLa couverture d’assurance est soumise aux conditions fournies au moment de la réservation et à la politique du partenaire local.\n\nCuracruze n’est pas responsable :\n• des dommages causés par un usage inapproprié du véhicule\n• des infractions aux lois locales de circulation\n• des amendes, pénalités ou confiscations\n\nLe client demeure responsable du véhicule pendant la période de location.\n\n7. Annulations et remboursements\n\nLes politiques d’annulation et de remboursement sont clairement indiquées pendant le processus de réservation.\n\nLes remboursements, le cas échéant, sont effectués via le mode de paiement initial et gérés par l’entité néerlandaise de Curacruze.\n\n8. Réclamations et assistance\n\nLes questions ou réclamations doivent être envoyées directement à Curacruze via les coordonnées disponibles sur le site. Curacruze coordonnera avec le partenaire local si nécessaire.\n\n9. Droit applicable\n\nLes présentes Conditions générales sont régies par le droit néerlandais. Tout litige sera soumis au tribunal compétent aux Pays-Bas, sauf disposition contraire de la loi locale impérative.\n\n10. Modifications\n\nCuracruze se réserve le droit de modifier ces Conditions générales à tout moment. La version publiée sur le site au moment de la réservation s’applique.\n\nInformations de contact\nCuracruze\nEntreprise individuelle néerlandaise\nChambre de commerce n° 86655124\nPays-Bas.",
      privacyTitle: "Politique de confidentialité",
      privacyContent: "1. Introduction\n\nCuracruze respecte votre vie privée et s’engage à protéger vos données personnelles. Cette Politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons les informations personnelles lorsque vous visitez notre site web ou utilisez nos services.\n\nCuracruze est une entreprise individuelle néerlandaise, légalement établie aux Pays-Bas et enregistrée auprès de la Chambre de commerce néerlandaise sous le numéro 86655124.\n\n2. Données personnelles collectées\n\nNous pouvons collecter et traiter les données personnelles suivantes :\n• Nom complet\n• Adresse e-mail\n• Numéro de téléphone\n• Informations de facturation et de paiement\n• Détails de réservation (dates de location, préférences de véhicule, lieu)\n• Communications avec le support client\n• Adresse IP et informations du navigateur (sécurité et analyses de base)\n\n3. Finalités du traitement\n\nNous traitons les données personnelles aux fins suivantes :\n• Gérer les réservations de location de voiture\n• Traiter les paiements et les remboursements\n• Communiquer avec les clients avant et pendant la période de location\n• Fournir une assistance client\n• Respecter les obligations légales et réglementaires\n• Prévenir la fraude et l’utilisation abusive de nos services\n\n4. Paiements\n\nLes paiements effectués via notre site web sont traités par des prestataires de paiement tiers sécurisés. Curacruze ne stocke pas les informations complètes de carte de paiement.\n\nTous les paiements sont traités via l’entité commerciale néerlandaise de Curacruze.\n\n5. Partage des données\n\nLes données personnelles peuvent être partagées uniquement lorsque cela est nécessaire :\n• Avec des partenaires locaux de location à Curaçao, uniquement pour exécuter la location\n• Avec des prestataires de paiement pour le traitement des transactions\n• Avec les autorités lorsque la loi l’exige\n\nNous ne vendons ni ne louons de données personnelles à des tiers.\n\n6. Transferts internationaux\n\nCertaines données personnelles peuvent être partagées avec des partenaires de service situés à Curaçao. Dans ce cas, Curacruze veille à ce que des garanties appropriées soient en place conformément aux lois applicables en matière de protection des données.\n\n7. Conservation\n\nLes données personnelles ne sont conservées que le temps nécessaire pour atteindre les objectifs décrits dans cette politique ou pour respecter les obligations légales.\n\n8. Sécurité\n\nCuracruze met en œuvre des mesures techniques et organisationnelles appropriées afin de protéger les données personnelles contre tout accès non autorisé, perte ou utilisation abusive.\n\n9. Vos droits\n\nConformément aux lois applicables, vous avez le droit de :\n• Accéder à vos données personnelles\n• Demander la correction ou la suppression\n• Vous opposer au traitement ou le limiter\n• Demander la portabilité des données\n\nLes demandes peuvent être soumises via les coordonnées ci-dessous.\n\n10. Cookies\n\nCuracruze utilise des cookies pour assurer le bon fonctionnement du site et améliorer l’expérience utilisateur. Des informations détaillées peuvent être fournies dans une Politique de cookies séparée.\n\n11. Modifications de cette politique\n\nCuracruze se réserve le droit de mettre à jour cette Politique de confidentialité à tout moment. La version publiée sur le site au moment de l’utilisation s’applique.\n\n12. Coordonnées\n\nCuracruze\nEntreprise individuelle néerlandaise\nChambre de commerce n° 86655124\nPays-Bas\n\nPour toute question relative à la confidentialité, veuillez nous contacter via les coordonnées fournies sur le site." 
    },

    // FAQ Section
    faq: {
      title: "Foire Aux Questions",
      subtitle: "Tout ce dont vous avez besoin de savoir pour louer avec CuraCruze",
      questions: {
        booking: {
          question: "Comment réserver une voiture avec CuraCruze ?",
          answer: "Parcourez simplement notre flotte, sélectionnez votre véhicule préféré, choisissez vos dates et terminez le processus de paiement sécurisé. Vous recevrez une confirmation immédiate par e-mail avec les instructions de prise en charge."
        },
        requirements: {
          question: "Quelles sont les conditions pour louer une voiture ?",
          answer: "Vous devez avoir au moins 21 ans et un permis de conduire valide détenu depuis au minimum 1 an. Les visiteurs internationaux ont besoin d'un passeport valide et d'un permis de conduire international."
        },
        payment: {
          question: "Quels moyens de paiement acceptez-vous ?",
          answer: "Nous acceptons les cartes (Visa, Mastercard) et iDEAL (EUR) via Stripe Checkout."
        },
        insurance: {
          question: "Les voitures CuraCruze sont-elles assurées ?",
          answer: "Oui, nous avons une couverture d'assurance ; elle est incluse avec chaque location."
        },
        fuel: {
          question: "Quelle est votre politique de carburant ?",
          answer: "Toutes les voitures sont fournies avec le réservoir plein et doivent être rendues avec le réservoir plein. Une station-service est idéalement située à 2 minutes de notre lieu de prise en charge."
        },
        pickup: {
          question: "Où puis-je récupérer et déposer la voiture ?",
          answer: "Nous proposons des options flexibles de prise en charge et de dépose, y compris les aéroports, les hubs urbains et même la livraison porte-à-porte dans certaines zones. Vérifiez la disponibilité lors de la réservation."
        }
      }
    },

    nav: {
      home: "Accueil",
      cars: "Voitures", 
      calendar: "Calendrier",
      about: "À Propos",
      contact: "Contact",
      bookNow: "Réserver",
      language: "Langue"
    },
    // Hero Section
    hero: {
      title: "Paradis Trouvé!",
      subtitle: "Votre aventure à Curaçao commence avec CuraCruze",
      cta1: "Commencer Mon Aventure!",
      cta2: "Découvrir le Paradis",
      description: "Chassez les couchers de soleil, découvrez des plages secrètes et parcourez l'île comme un local - votre aventure à Curaçao commence dès que vous avez les clés en main.",
      tagline: "Soleil, mer et détours spontanés… on y va!",
      vacationBadge: "Votre Paradis Vous Attend!",
      driveYourDreams: "Conduisez Vos Rêves", 
      chips: {
        sun: "Soleil",
        beaches: "Plages",
        adventure: "Aventure"
      },
      memoriesWaiting: "Souvenirs En Attente",
      stats: {
        sunnyDays: "Jours Ensoleillés/An",
        beaches: "Plages Vierges",
        experiences: "Expériences Uniques",
        satisfied: "Clients Satisfaits"
      }
    },
    // Cars Section
    cars: {
      sectionTitle: "Vos Vacances Commencent Ici!",
      sectionSubtitle: "Prêt à réaliser vos rêves de Curaçao? Nous avons tout ce dont vous avez besoin pour l'escapade parfaite sur l'île!",
      fleetTitle: "Notre Flotte",
      fleetSubtitle: "Les Clés De La Liberté",
      viewAll: "Voir Toutes les Voitures",
      bookNow: "Réserver",
      available: "Disponible",
      unavailable: "Non Disponible",
      nextAvailable: "Prochaine Disponible",
      seats: "sièges",
      lowSeason: "Basse Saison",
      highSeason: "Haute Saison", 
      weeklyFrom: "Hebdomadaire à partir de",
      loading: "Chargement de notre flotte incroyable...",
      compact: "Compact",
      suv: "SUV", 
      sedan: "Berline",
      automatic: "Automatique",
      manual: "Manuelle",
      availability: "Disponibilité"
    },
    carsPage: {
      bookingInfo: {
        title: "Informations de Réservation",
        bookWithUs: "Réservez Avec Nous",
        whatsapp: "WhatsApp",
        email: "Email",
        reservationFee: "Frais de réservation : XCG 150 (déduits du prix total de location)",
        cancellationPolicy: "Politique d'annulation",
        cancel48: "Annulation gratuite jusqu'à 48 heures avant la prise en charge",
        cancel48to24: "Annulation dans les 48 h = 50 % des frais de réservation perdus",
        cancel24: "Annulation dans les 24 h = 100 % des frais de réservation perdus",
        rentalTerms: "Conditions de location",
        termNoDeposit: "Aucun dépôt requis, seulement les frais de réservation",
        termCleanFullTank: "Voitures livrées propres et avec le réservoir plein – veuillez rendre dans le même état",
        termInsured: "Tous les véhicules sont bien assurés",
        termPickupDropoff: "Service de prise en charge/dépose disponible",
        termExtras: "Extras comme sièges enfants ou conducteur supplémentaire sur demande."
      }
    },
    aboutPage: {
      hero: {
        title: "À propos de CuraCruze",
        subtitle: "Votre aventure à Curaçao commence avec CuraCruze"
      },
      intro: {
        title: "CuraCruze Location de Voitures",
        description: "Curacruze est une entreprise individuelle néerlandaise (Chambre de commerce n° 86655124), légalement établie aux Pays-Bas. Depuis les Pays-Bas, nous proposons des services de location de voitures à Curaçao pour les voyageurs recherchant une expérience fiable et transparente.\n\nLa location de voitures à Curaçao est fournie en coopération avec des partenaires locaux soigneusement sélectionnés. Curacruze agit en tant que prestataire de services et principal point de contact pour les clients, tandis que les véhicules sont mis à disposition localement par nos partenaires.\n\nToutes les réservations et les paiements sont traités via notre entité commerciale néerlandaise. Nous assurons une communication claire, un support avant l’arrivée et une assistance tout au long de la période de location, afin que nos clients profitent d’un séjour sans souci à Curaçao.\n\nSi vous avez des questions, n’hésitez pas à nous contacter   nous serons ravis de vous aider."
      },
      why: {
        title: "Pourquoi choisir CuraCruze ?",
        reliable: {
          title: "Service Fiable",
          description: "Nous sommes disponibles 24h/24 et 7j/7. De votre arrivée jusqu'à votre retour à l'aéroport, nous vous accompagnons."
        },
        transparent: {
          title: "Tarifs Transparents",
          description: "Aucun frais caché. Des accords clairs et des prix justes."
        },
        pickup: {
          title: "Prise en charge et dépose gratuites",
          description: "Nous livrons votre voiture de location à votre hébergement ou à l'aéroport, gratuitement."
        },
        vehicles: {
          title: "Véhicules modernes et bien entretenus",
          description: "Nos voitures sont propres, bien assurées et entretenues régulièrement pour votre tranquillité."
        },
        local: {
          title: "Local et Personnel",
          description: "Nous connaissons Curaçao sur le bout des doigts et partageons volontiers nos conseils sur les meilleures plages, restaurants et trésors cachés."
        },
        closing: {
          line1: "Réservez votre voiture dès aujourd'hui et explorez Curaçao à votre rythme.",
          line2: "Des questions ? Contactez-nous   nous serons ravis de vous aider !"
        }
      },
      mission: {
        title: "Notre mission",
        description: "Chez CuraCruze, ce n’est pas seulement une question de voitures   c’est une question d’expérience. Nous voulons que vous vous sentiez libre, en sécurité et comme chez vous sur l’île. C’est pourquoi nous faisons toujours un pas de plus pour vous."
      },
      serve: {
        title: "Qui nous servons",
        description: "Vous voyagez seul, en couple, en famille ou entre amis ? Nous avons des voitures compactes, des SUV spacieux et tout ce qu’il faut entre les deux. Vous restez plus longtemps ? Demandez nos réductions longue durée."
      },
      cta: {
        title: "Prêt à explorer Curaçao ?",
        subtitle: "Contactez-nous dès aujourd'hui pour réserver votre voiture idéale et commencer votre aventure sur l'île !",
        browseFleet: "Voir notre flotte",
        contactUs: "Nous contacter"
      }
    },
    // Reviews Section
    reviews: {
      title: "Ce Que Disent Nos Clients",
      subtitle: "De vraies expériences de voyageurs heureux",
      trustpilot: "Excellent • 4,8/5 • Basé sur 200+ avis",
      testimonials: {
        thijs: {
          quote: "La connaissance locale est inestimable ! L'équipe a partagé des plages secrètes et des restaurants incroyables que nous n'aurions jamais trouvés. La voiture était parfaite et le service était de classe mondiale.",
          name: "Thijs Hendriks",
          role: "Passionné de photographie"
        },
        eva: {
          quote: "Voitures propres, prix justes et vraie attention aux clients. CuraCruze a transformé nos vacances en aventure ! Nous planifions déjà notre prochain voyage et réserverons à nouveau sans hésiter.",
          name: "Eva de Jong",
          role: "Cliente fidèle"
        },
        lars: {
          quote: "Service professionnel du début à la fin. La voiture était impeccable et exactement comme décrite. Excellent rapport qualité-prix. Je referai appel à CuraCruze sans hésiter !",
          name: "Lars van Dam",
          role: "Voyageur d'affaires"
        },
        anna: {
          quote: "Au-delà de nos attentes ! L'équipe nous a donné d'excellentes recommandations de plages cachées et de restaurants locaux. La voiture était impeccable et le service exceptionnel.",
          name: "Anna Jansen",
          role: "Escapade en couple"
        },
        larsB: {
          quote: "La meilleure expérience de location de voiture ! La Chevrolet Spark était parfaite pour la conduite en ville et le stationnement près des plages. La prise en charge et la dépose gratuites ont rendu tout très simple.",
          name: "Lars Bakker",
          role: "Amateur d'aventure"
        },
        sophie: {
          quote: "CuraCruze a rendu nos vacances en famille parfaites ! La Kia Rio était spacieuse et confortable. Les sièges enfants étaient propres et correctement installés. Excellent service du début à la fin !",
          name: "Sophie Mulder",
          role: "Vacances en famille"
        },
        daan: {
          quote: "Tarifs transparents et aucun frais caché   exactement comme promis ! La réservation s'est déroulée sans problème et l'équipe a été incroyablement utile avec des conseils locaux et des indications.",
          name: "Daan van Leeuwen",
          role: "Premier séjour"
        },
        lotte: {
          quote: "Le support 24/7 nous a sauvés ! Un petit souci tard le soir, résolu immédiatement. De vrais professionnels qui prennent soin de leurs clients. Merci CuraCruze !",
          name: "Lotte Smit",
          role: "Nomade digital"
        }
      }
    },
    common: {
      readMore: "Lire la suite",
      readLess: "Réduire"
    },
    carDescriptions: {
      budgetExplorer: "Parfait pour les voyageurs au budget serré. Cette berline Nissan Versa blanche et impeccable offre un confort fiable et économe pour parcourir Curaçao en toute simplicité. Assurance CDW incluse.",
      islandCruiser: "Démarquez-vous avec cette Kia Soul bleue vibrante idéale pour passer de plage en plage et explorer les routes panoramiques de Curaçao. Intérieur spacieux et conduite facile et fun. Assurance CDW incluse.",
      coastalComfort: "Élégante berline Kia Rio blanche, photographiée le long de la magnifique côte de Curaçao. Confortable, stylée et prête pour toutes vos aventures sur l'île. Assurance CDW incluse.",
      beachRunner: "Le compagnon de plage ultime ! Cette Chevrolet Spark blanche compacte est parfaite pour la conduite en ville et se garer facilement près des plages les plus populaires de Curaçao. Assurance CDW incluse."
    },
    // Secret Paradise Spots
    paradise: {
      title: "Lieux Secrets du Paradis!",
      subtitle: "Découvrez des joyaux cachés que seuls les habitants connaissent",
      hiddenBeaches: {
        title: "Plages Cachées",
        description: "Plages de sable blanc isolées parfaites pour les escapades romantiques et la détente paisible.",
        locations: "Playa Lagun: Crique isolée avec tortues • Playa Jeremi: Paisible et époustouflant • Playa Gipy: Le secret le mieux gardé de l'île • Playa Daaibari: Joyau tranquille de la côte nord"
      },
      secretSnorkeling: {
        title: "Plongée Secrète",
        description: "Mondes sous-marins vierges avec des récifs coralliens colorés et des poissons tropicaux.",
        locations: "Playa Piskado: Plongée avec tortues de mer • Tugboat Beach: Aventure de remorqueur coulé • Playa Kalki: Formations coralliennes vibrantes • Playa Santu Pretu: Site de plongée sur épave"
      },
      magicalSunsets: {
        title: "Couchers de Soleil Magiques",
        description: "Points de vue époustouflants pour des moments inoubliables de l'heure dorée.",
        locations: "Knip Beach: Couchers de soleil aux eaux turquoise • Mambo Beach: Ambiance nocturne animée • Shete Boka: Vues dramatiques sur la côte • Klein Curacao: Paradis d'île inhabitée"
      },
      cta: "Louez une Voiture & Explorez Ces Joyaux Cachés!",
      tip: "*Demandez à notre équipe des directions détaillées et des conseils d'initiés lors de la récupération de votre voiture"
    },
    // Popular Destinations
    destinations: {
      title: "Destinations Populaires",
      subtitle: "Lieux incontournables autour de l'île",
      pundaOtrobanda: {
        title: "Punda-Otrobanda",
        name: "Punda-Otrobanda",
        description: "Willemstad historique avec architecture coloniale hollandaise colorée et statut de patrimoine mondial UNESCO."
      },
      janthiel: {
        title: "Plage de Janthiel", 
        name: "Janthiel",
        description: "Plage vierge aux eaux cristallines, parfaite pour nager et faire de la plongée."
      },
      mambo: {
        title: "Plage Mambo",
        name: "Plage Mambo", 
        description: "Scène de club de plage dynamique avec restaurants, bars et activités de sports nautiques."
      }
    },
    // Service Excellence Section
    services: {
      title: "Excellence du Service CuraCruze",
      subtitle: "Tout ce dont vous avez besoin pour une expérience parfaite de location de voiture à Curaçao",
      insurance: {
        title: "Assurance Auto",
        description: "Chaque véhicule CuraCruze inclut une couverture d'assurance complète, pour que vous puissiez profiter de votre voyage sans stress."
      },
      pickup: {
        title: "Service de Prise en Charge & Dépose",
        description: "Avec CuraCruze, vous profitez d'un service gratuit de prise en charge et de dépose, que ce soit à l'aéroport de Hato ou à votre hébergement. Votre confort est notre priorité."
      },
      service: {
        title: "Excellent Service",
        description: "Chez CuraCruze, nous sommes fiers d'offrir un service amical, fiable et professionnel – à chaque étape. Comptez sur nous pour un service excellent et sans tracas du début à la fin."
      },
      reservation: {
        title: "Réservation",
        description: "Nous exigeons une réservation au moins 3 jours à l'avance. Le coût de la réservation est de XCG 150,-. Ce montant sera déduit du prix total."
      },
      cleanCar: {
        title: "Voiture Propre - Réservoir Plein",
        description: "Nous fournissons la voiture propre et avec le réservoir plein. Nous apprécierions beaucoup que la voiture soit rendue dans le même état   propre et avec le réservoir plein."
      },
      family: {
        title: "Voyager en Famille",
        description: "Nous fournissons des sièges enfants adaptés aux enfants de 3 à 12 ans. Le coût est de XCG 5,- par siège et par jour."
      }
    }
  }
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('curacruze-language') || 'en';
  });

  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('curacruze-currency') || 'EUR';
  });

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('curacruze-language', newLanguage);
  };

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('curacruze-currency', newCurrency);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  const formatMoney = (usdAmount) => {
    if (usdAmount === null || usdAmount === undefined) return '';

    const amountNum = Number(usdAmount);
    if (Number.isNaN(amountNum)) return '';

    // XCG is display-only. We still store bookings/payments in USD or EUR.
    if (currency === 'XCG') {
      const xcg = Math.round(amountNum * USD_TO_XCG_RATE);
      return `XCG ${xcg}`;
    }

    if (currency === 'EUR') {
      const eur = Math.round(amountNum * USD_TO_EUR_RATE);
      return `€${eur}`;
    }

    // USD default
    const usd = Math.round(amountNum);
    return `$${usd}`;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      <CurrencyContext.Provider value={{ currency, changeCurrency, formatMoney }}>
        {children}
      </CurrencyContext.Provider>
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Custom hook to use currency context
const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a LanguageProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Payment Success Page Component

const AdminDashboardPage = () => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken') || '');
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState('');

  const fetchBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/bookings`, {
        headers: {
          'X-Admin-Token': adminToken
        }
      });
      setBookings(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // only auto-fetch if token exists
    if (adminToken) {
      fetchBookings();
    } else {
      setLoading(false);
    }
  }, []);

  const saveToken = () => {
    localStorage.setItem('adminToken', adminToken);
    fetchBookings();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-10" data-testid="admin-dashboard">
        <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">Admin</h1>
            <p className="text-base md:text-lg text-gray-600 mt-2">Bookings overview (data from database; Stripe is not queried here).</p>
          </div>
          <div className="w-full md:w-[520px] bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-gray-800 mb-2">Admin Token</div>
            <div className="flex gap-2">
              <input
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Paste X-Admin-Token"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
              />
              <Button onClick={saveToken} className="bg-gray-900 text-white hover:bg-gray-800">Load</Button>
            </div>
            {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
          </div>
        </div>

        <div className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="font-semibold text-gray-900">Latest bookings</div>
            <Button variant="outline" onClick={fetchBookings} disabled={!adminToken || loading}>Refresh</Button>
          </div>

          {loading ? (
            <div className="p-8 text-gray-600">Loading...</div>
          ) : bookings.length === 0 ? (
            <div className="p-8 text-gray-600">No bookings found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="text-left font-semibold px-4 py-3">Car</th>
                    <th className="text-left font-semibold px-4 py-3">Rental period</th>
                    <th className="text-left font-semibold px-4 py-3">Days</th>
                    <th className="text-left font-semibold px-4 py-3">Customer</th>
                    <th className="text-left font-semibold px-4 py-3">Phone</th>
                    <th className="text-left font-semibold px-4 py-3">Email</th>
                    <th className="text-left font-semibold px-4 py-3">Address</th>
                    <th className="text-left font-semibold px-4 py-3">Amount (€)</th>
                    <th className="text-left font-semibold px-4 py-3">Paid at</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const paid = (b.status || '').toLowerCase() === 'paid';
                    const statusLabel = paid ? 'Paid' : 'Pending';
                    const statusClass = paid ? 'bg-green-50' : 'bg-yellow-50';
                    return (
                      <tr key={b.bookingId} className={`border-t border-gray-100 ${statusClass}`}>
                        <td className="px-4 py-3">
                          <Badge className={paid ? 'bg-green-600' : 'bg-yellow-600'}>{statusLabel}</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-900">{b.car_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{(b.start_date || '-') + ' → ' + (b.end_date || '-')}</td>
                        <td className="px-4 py-3 text-gray-900">{b.days ?? '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{b.customer_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{b.customer_phone || '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{b.customer_email || '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{(b.customer_street_name && b.customer_house_number && b.customer_city) ? `${b.customer_street_name} ${b.customer_house_number}, ${b.customer_city}` : '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{(typeof b.amount === 'number') ? b.amount.toFixed(2) : '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{b.paid_at || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentSuccessPage = () => {
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [bookingDetails, setBookingDetails] = useState(null);

  const fireConfetti = () => {
    const colors = ['#0a2a66', '#1e3a8a', '#facc15', '#fde047'];
    const end = Date.now() + 1500;
    const burst = (origin) => confetti({
      particleCount: 120,
      spread: 80,
      startVelocity: 45,
      origin,
      colors,
      zIndex: 9999,
    });
    burst({ x: 0.5, y: 0.4 });
    (function frame() {
      confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors, zIndex: 9999 });
      confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors, zIndex: 9999 });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setPaymentStatus('error');
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      // Check which payment provider was used (default to Stripe)
      const paymentProvider = localStorage.getItem('paymentProvider') || 'stripe';
      const endpoint = `${API}/payments/status/${sessionId}`;
      
      const response = await axios.get(endpoint);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        fireConfetti();
        
        // Get booking details
        const bookingResponse = await axios.get(`${API}/bookings/${data.booking_id}`);
        setBookingDetails(bookingResponse.data);
        
        // Clear pending booking ID and payment provider
        localStorage.removeItem('pendingBookingId');
        localStorage.removeItem('paymentProvider');
        
        toast.success(`Payment successful! Booking confirmed.`);
        return;
      } else if (data.payment_status === 'expired' || data.payment_status === 'failed') {
        setPaymentStatus('failed');
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  const formatCurrency = (amount, currency) => {
    const currencySymbols = { USD: '$', EUR: '€', XCG: 'XCG', ANG: 'ƒ' };

    if (currency === 'XCG') {
      const xcg = Math.round(Number(amount) * USD_TO_XCG_RATE);
      return `${currencySymbols.XCG} ${xcg}`;
    }

    const value = Math.round(amount);
    return `${currencySymbols[currency] || currency} ${value}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {paymentStatus === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {paymentStatus === 'success' && bookingDetails && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-3">Your booking has been confirmed. You should receive a confirmation email shortly.</p>
            <p className="text-lg font-semibold mb-6" style={{ color: '#0a2a66' }} data-testid="vacation-wish">
              🌴 We wish you a wonderful vacation in Curaçao! 🌞
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Booking Details</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Booking ID:</strong> {bookingDetails.id.substring(0, 8)}...</p>
                <p><strong>Total Amount:</strong> {formatCurrency(bookingDetails.total_amount, bookingDetails.currency)}</p>
                <p><strong>Pickup Date:</strong> {new Date(bookingDetails.pickup_date).toLocaleDateString()}</p>
                <p><strong>Return Date:</strong> {new Date(bookingDetails.return_date).toLocaleDateString()}</p>
              </div>
            </div>
            
            <Button 
              onClick={() => window.location.href = '/'}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              Return to Home
            </Button>
          </>
        )}

        {(paymentStatus === 'failed' || paymentStatus === 'error' || paymentStatus === 'timeout') && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Payment {paymentStatus === 'timeout' ? 'Timeout' : 'Failed'}</h2>
            <p className="text-gray-600 mb-6">
              {paymentStatus === 'timeout' 
                ? 'Payment verification timed out. Please check your email for confirmation or contact us.'
                : 'There was an issue processing your payment. Please try again or contact us for assistance.'
              }
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.href = '/cars'}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              >
                Try Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                Return to Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Payment Cancelled Page Component
const PaymentCancelledPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-yellow-600 mb-2">Payment Cancelled</h2>
        <p className="text-gray-600 mb-6">
          Your payment was cancelled. No charges were made to your account. You can try booking again when you're ready.
        </p>
        
        <div className="space-y-3">
          <Button 
            onClick={() => window.location.href = '/cars'}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            Try Again
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.location.href = '/'}
            className="w-full"
          >
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

const VideoSlideshow = () => {
  const slides = [
    { mp4: "/videos/curacao_4k.mp4", webm: "/videos/curacao_720.webm", poster: "/videos/curacao_poster.jpg" },
    { mp4: "/videos/strand_4k.mp4", webm: "/videos/strand_720.webm", poster: "/videos/strand_poster.jpg" },
    { mp4: "/videos/zee_4k.mp4", webm: "/videos/zee_720.webm", poster: "/videos/zee_poster.jpg" },
    { mp4: "/videos/beach_4k.mp4", webm: "/videos/beach_720.webm", poster: "/videos/beach_poster.jpg" },
  ];
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="mt-12" data-testid="video-slideshow">
      <div className="relative w-full max-w-5xl mx-auto aspect-video rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
        {slides.map((s, i) => (
          <video
            key={s.mp4}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={s.poster}
            data-testid={`video-slide-${i}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: i === active ? 1 : 0 }}
          >
            <source src={s.mp4} type="video/mp4" />
            <source src={s.webm} type="video/webm" />
          </video>
        ))}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`video-dot-${i}`}
              className={`h-2.5 rounded-full transition-all ${i === active ? "w-8 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SiteFooter = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <Car className="w-8 h-8 text-orange-500" />
              <span className="text-2xl font-bold">CuraCruze</span>
            </div>
            <p className="text-gray-400 mb-4">{t('footer.description')}</p>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">{t('footer.legalNote')}</p>
            <div className="flex space-x-4 items-center">
              <a
                href="https://www.facebook.com/profile.php?id=61577944107527"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
                className="block hover:scale-110 transition-transform"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_b7f8b92b-389f-4780-82a5-24dc8e8ac98a/artifacts/bz8q3e78_Schermafbeelding%202026-06-23%20203420.png"
                  alt="Facebook"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </a>
              <a
                href="https://www.instagram.com/cura.cruze/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
                className="block hover:scale-110 transition-transform"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_b7f8b92b-389f-4780-82a5-24dc8e8ac98a/artifacts/rdjqgrsa_Schermafbeelding%202025-06-27%20192315.png"
                  alt="Instagram"
                  className="w-9 h-9 rounded-lg object-cover"
                />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              <li><a href="/cars" className="text-gray-400 hover:text-white transition-colors">{t('footer.ourFleet')}</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t('footer.pricing')}</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t('footer.locations')}</a></li>
              <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t('footer.aboutUs')}</a></li>
              <li>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="terms" className="border-none">
                    <AccordionTrigger className="py-0 text-left text-gray-400 hover:text-white hover:no-underline outline-none ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0">
                      {t('footer.termsTitle')}
                    </AccordionTrigger>
                    <AccordionContent className="pt-3">
                      <div className="text-sm text-gray-400 whitespace-pre-line max-h-80 overflow-y-auto pr-2">
                        {t('footer.termsContent')}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="privacy" className="border-none mt-3">
                    <AccordionTrigger className="py-0 text-left text-gray-400 hover:text-white hover:no-underline outline-none ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0">
                      {t('footer.privacyTitle')}
                    </AccordionTrigger>
                    <AccordionContent className="pt-3">
                      <div className="text-sm text-gray-400 whitespace-pre-line max-h-80 overflow-y-auto pr-2">
                        {t('footer.privacyContent')}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </li>
            </ul>
          </div>


          <div>
            <h3 className="text-xl font-semibold mb-6">{t('footer.contact')}</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-orange-500 mr-3 mt-0.5" />
                <div className="text-gray-400">
                  <div className="font-medium text-white/90">{t('footer.businessAddress')}</div>
                  <div className="text-gray-400">{t('footer.businessAddressLine')}</div>
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-orange-500 mr-3">📞</span>
                <span className="text-gray-400">{t('footer.phoneNumber')}</span>
              </div>
              <div className="flex items-center">
                <span className="text-orange-500 mr-3">✉️</span>
                <span className="text-gray-400">info@curacruze.com</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center">
          <p className="text-gray-400">{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

const AboutPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">{t('aboutPage.hero.title')}</h1>
          <p className="text-2xl text-orange-100 max-w-3xl mx-auto">
            {t('aboutPage.hero.subtitle')}
          </p>
        </div>
      </div>

      {/* About Us Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('aboutPage.intro.title')}</h2>
              <p className="text-xl text-gray-700 leading-relaxed whitespace-pre-line">
                {t('aboutPage.intro.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose CuraCruze */}
      <div className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('aboutPage.why.title')}</h2>
          </div>
          
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutPage.why.reliable.title')}</h3>
                  <p className="text-gray-700">{t('aboutPage.why.reliable.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutPage.why.transparent.title')}</h3>
                  <p className="text-gray-700">{t('aboutPage.why.transparent.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutPage.why.pickup.title')}</h3>
                  <p className="text-gray-700">{t('aboutPage.why.pickup.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutPage.why.vehicles.title')}</h3>
                  <p className="text-gray-700">{t('aboutPage.why.vehicles.description')}</p>
                </div>
              </div>

 

            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutPage.why.local.title')}</h3>
                    <p className="text-gray-700">{t('aboutPage.why.local.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-xl text-gray-700 mb-6">
              {t('aboutPage.why.closing.line1')}
            </p>
            <p className="text-lg text-gray-600">
              {t('aboutPage.why.closing.line2')}
            </p>
          </div>
        </div>
      </div>

      {/* Mission and Who We Serve */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="text-center lg:text-left">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('aboutPage.mission.title')}</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('aboutPage.mission.description')}
                </p>
              </div>
              
              <div className="text-center lg:text-left">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('aboutPage.serve.title')}</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('aboutPage.serve.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Call to Action */}
      <div className="py-20 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{t('aboutPage.cta.title')}</h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">{t('aboutPage.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={() => window.location.href = '/cars'}
              data-testid="about-browse-cars"
            >
              {t('aboutPage.cta.browseFleet')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Page Component
const CalendarPage = () => {
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'single'

  useEffect(() => {
    fetchCarsAndCalendarData();
  }, [currentDate]);

  const fetchCarsAndCalendarData = async () => {
    try {
      setLoading(true);
      
      // Fetch cars
      const carsResponse = await axios.get(`${API}/cars`);
      const carsData = carsResponse.data;
      setCars(carsData);
      
      // Set first car as selected by default
      if (carsData.length > 0 && !selectedCar) {
        setSelectedCar(carsData[0]);
      }
      
      // Get date range (current month + next 2 months)
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 0);
      
      // Fetch calendar data for all cars
      const calendarResponse = await axios.get(`${API}/availability/calendar`, {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      
      setCalendarData(calendarResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateUnavailable = (date, carId) => {
    const carData = calendarData[carId];
    if (!carData || !date) return false;
    
    const checkDate = date.toISOString().split('T')[0];
    
    return carData.unavailable_dates.some(period => {
      const startDate = period.start_date;
      const endDate = period.end_date;
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const getDateStatus = (date, carId) => {
    if (!date) return 'empty';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return 'past';
    if (isDateUnavailable(date, carId)) return 'unavailable';
    return 'available';
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Car Availability Calendar</h1>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/cars'}
            >
              Back to Fleet
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* View Mode Toggle */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">Calendar View</h2>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'all' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All Cars
                </button>
                <button
                  onClick={() => setViewMode('single')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'single' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Single Car
                </button>
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-200 border border-green-400 rounded"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-200 border border-red-400 rounded"></div>
                <span>Unavailable</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 border border-gray-300 rounded"></div>
                <span>Past Date</span>
              </div>
            </div>
          </div>

          {/* Car Selection for Single Car View */}
          {viewMode === 'single' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Car:
              </label>
              <Select value={selectedCar?.id || ''} onValueChange={(value) => {
                const car = cars.find(c => c.id === value);
                setSelectedCar(car);
              }}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Choose a car" />
                </SelectTrigger>
                <SelectContent>
                  {cars.map(car => (
                    <SelectItem key={car.id} value={car.id}>
                      {car.name} ({car.brand} {car.model})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(-1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {viewMode === 'single' && selectedCar ? (
            // Single Car Calendar
            <div>
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  {selectedCar.name} - {selectedCar.brand} {selectedCar.model}
                </h4>
              </div>
              
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">

 

                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth(currentDate).map((date, index) => {
                  const status = getDateStatus(date, selectedCar.id);
                  
                  return (
                    <div
                      key={index}
                      className={`p-3 text-center text-sm border rounded cursor-pointer hover:opacity-80 transition-opacity ${
                        status === 'available'
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : status === 'unavailable'
                          ? 'bg-red-100 border-red-300 text-red-800'
                          : status === 'past'
                          ? 'bg-gray-100 border-gray-300 text-gray-500'
                          : 'border-gray-200'
                      }`}
                    >
                      {date ? date.getDate() : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // All Cars Overview
            <div>
              <div className="space-y-8">
                {cars.map(car => (
                  <div key={car.id} className="border-b pb-6 last:border-b-0">
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-gray-800">
                        {car.name} - {car.brand} {car.model}
                      </h4>
                    </div>
                    
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {getDaysInMonth(currentDate).map((date, index) => {
                        const status = getDateStatus(date, car.id);
                        
                        return (
                          <div
                            key={index}
                            className={`p-2 text-center text-xs border rounded ${
                              status === 'available'
                                ? 'bg-green-100 border-green-300 text-green-800'
                                : status === 'unavailable'
                                ? 'bg-red-100 border-red-300 text-red-800'
                                : status === 'past'
                                ? 'bg-gray-100 border-gray-300 text-gray-500'
                                : 'border-gray-200'
                            }`}
                          >
                            {date ? date.getDate() : ''}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Homepage Component
const HomePage = () => {
  const { t, language } = useLanguage();
  const { currency, changeCurrency, formatMoney } = useCurrency();
  
  // Cars functionality for embedded section
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carAvailability, setCarAvailability] = useState({});
  const [selectedCar, setSelectedCar] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  useEffect(() => {
    const fetchCars = async () => {
      try {
        setLoading(true);
        
        // Fetch cars
        const carsResponse = await axios.get(`${API}/cars`);
        const carsData = carsResponse.data;
        setCars(carsData);
        
        // Fetch availability for each car (next 30 days)
        const today = new Date();
        const thirtyDaysLater = new Date();
        thirtyDaysLater.setDate(today.getDate() + 30);
        
        const availabilityPromises = carsData.map(async (car) => {
          try {
            const response = await axios.get(`${API}/cars/${car.id}/availability`, {
              params: {
                start_date: today.toISOString().split('T')[0],
                end_date: thirtyDaysLater.toISOString().split('T')[0]
              }
            });
            
            const unavailableDates = response.data.unavailable_dates || [];
            const nextAvailableDate = findNextAvailableDate(today, unavailableDates);
            
            return {
              carId: car.id,
              unavailableDates,
              nextAvailableDate,
              isCurrentlyAvailable: isDateAvailable(today, unavailableDates)
            };
          } catch (error) {
            console.error(`Error fetching availability for car ${car.id}:`, error);
            return {
              carId: car.id,
              unavailableDates: [],
              nextAvailableDate: today,
              isCurrentlyAvailable: true
            };
          }
        });
        
        const availabilityResults = await Promise.all(availabilityPromises);
        const availabilityMap = {};
        availabilityResults.forEach(result => {
          availabilityMap[result.carId] = result;
        });
        
        setCarAvailability(availabilityMap);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching cars:', error);
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  const handleBookNow = (car) => {
    setSelectedCar(car);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      console.log('Creating booking:', bookingData);
      
      const paymentCurrency = 'EUR';

      const bookingPayload = {
        car_id: selectedCar.id,
        currency: paymentCurrency,
        customer_name: bookingData.customer_name,
        customer_email: bookingData.customer_email,
        customer_phone: bookingData.customer_phone,
        pickup_date: bookingData.pickup_date,
        return_date: bookingData.return_date,
        pickup_location: bookingData.pickup_location,
        return_location: bookingData.return_location,
        special_requests: bookingData.special_requests || ''
      };

      const bookingResponse = await axios.post(`${API}/bookings`, bookingPayload);
      const booking = bookingResponse.data;
      
      console.log('Booking created:', booking);

      const currency = bookingPayload.currency || 'USD';
      let paymentEndpoint;
      let paymentProvider;

      // Stripe is now the primary payment provider
      paymentEndpoint = `${API}/payments/create-checkout`;
      paymentProvider = 'stripe';


      localStorage.setItem('paymentProvider', paymentProvider);
      localStorage.setItem('pendingBookingId', booking.id);

      // Client-side required address validation
      if (!bookingData.street_name?.trim() || !bookingData.house_number?.trim() || !bookingData.city?.trim()) {
        throw new Error('Address is required: Street name, House number, City');
      }

      const paymentPayload = {
        booking_id: booking.id,
        street_name: bookingData.street_name.trim(),
        house_number: bookingData.house_number.trim(),
        city: bookingData.city.trim()
      };

      console.log(`Creating ${paymentProvider} payment with:`, paymentPayload);
      
      const paymentResponse = await axios.post(paymentEndpoint, paymentPayload);
      const paymentData = paymentResponse.data;
      
      console.log('Payment response:', paymentData);

      if (paymentData.checkout_url) {
        console.log('Redirecting to:', paymentData.checkout_url);
        window.location.href = paymentData.checkout_url;
      } else {
        throw new Error('No checkout URL received from payment provider');
      }

    } catch (error) {
      console.error('Booking submission error:', error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        alert(`Error processing your booking: ${detail.map((d) => d?.msg).filter(Boolean).join(' | ') || error.message}`);
      } else {
        alert(`Error processing your booking: ${detail || error.message}`);
      }
    }
  };
  return (
    <div className="relative min-h-screen">
      {/* Joyful Sunny Hero Section */}
      <div 
        className="relative h-screen bg-cover bg-center bg-no-repeat flex items-center overflow-hidden"
        style={{
          // Hero image credit (not shown in UI):
          // "Handelskade Willemstad - Curacao" by Fraganda, CC BY-SA 4.0
          // Source: https://commons.wikimedia.org/wiki/File:Handelskade_Willemstad_-_Curacao.jpg
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.55)), url('https://upload.wikimedia.org/wikipedia/commons/f/f3/Handelskade_Willemstad_-_Curacao.jpg')`
        }}
      >
        {/* Subtle light blobs */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-10 -right-10 w-44 h-44 bg-yellow-300/70 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute top-24 right-20 w-32 h-32 bg-orange-300/60 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: '1.5s' }}
          ></div>
          <div
            className="absolute top-52 right-6 w-28 h-28 bg-cyan-300/50 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: '3s' }}
          ></div>
        </div>
        
        {/* Tropical overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 via-yellow-300/10 to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            {/* Sticker badge (subtle float) */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-5 py-2 rounded-full text-sm font-semibold mb-5 shadow-lg hero-badge-float -rotate-2">
              {t('hero.vacationBadge')}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight text-white drop-shadow-lg">
              <span className="bg-gradient-to-r from-yellow-200 to-orange-200 bg-clip-text text-transparent">
                {t('hero.title').split(' ')[0]} 
              </span>
              <span className="text-white"> {t('hero.title').split(' ')[1]}!</span>
              <div className="mt-3">
                <span className="text-2xl sm:text-3xl lg:text-4xl bg-gradient-to-r from-blue-200 to-cyan-200 bg-clip-text text-transparent">
                  {t('hero.driveYourDreams')}
                </span>
                
              </div>
            </h1>

            
            <p className="text-base sm:text-lg mb-8 text-white font-medium leading-relaxed drop-shadow">
              {t('hero.description')}
              <span className="block mt-2 text-base sm:text-lg text-white/95">
                {t('hero.tagline')}
              </span>
            </p>
            
            {/* Vacation stats */}
            <div className="flex gap-8 mb-8 text-white">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-300">350+</div>
                <div className="text-sm text-yellow-100">{t('hero.stats.sunnyDays')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-300">35+</div>
                <div className="text-sm text-cyan-100">{t('hero.stats.beaches')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-300">∞</div>
                <div className="text-sm text-orange-100">{t('hero.memoriesWaiting')}</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 hover:from-yellow-500 hover:via-orange-500 hover:to-red-500 text-white px-10 py-5 text-xl font-bold shadow-2xl transform hover:scale-105 transition-all duration-300 border-2 border-yellow-300"
                data-testid="browse-cars-btn"
                onClick={() => window.location.href = '/cars'}
              >
                {t('hero.cta1')}
              </Button>
              <Button 
                size="lg" 
                className="bg-white/20 backdrop-blur-sm border-2 border-white/50 text-white hover:bg-white hover:text-orange-600 px-10 py-5 text-xl font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                data-testid="learn-more-btn"
              >
                {t('hero.cta2')}
              </Button>
            </div>
            
          </div>
        </div>
        
        {/* Floating paradise indicators */}
      </div>


      {/* Popular Destinations */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('cars.sectionTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('cars.sectionSubtitle')}</p>

            <div className="mt-6">
              <div className="text-2xl font-bold text-gray-900">{t('destinations.title')}</div>
              <div className="text-base text-gray-600">{t('destinations.subtitle')}</div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group cursor-pointer">
              <div className="overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow">
                <img 
                  src="https://images.unsplash.com/photo-1648512557684-425488b7659d?w=600&h=400&fit=crop" 
                  alt="Punda-Otrobanda" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('destinations.pundaOtrobanda.title')}</h3>
                  <p className="text-gray-600">{t('destinations.pundaOtrobanda.description')}</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_island-cruiser/artifacts/wle9phwd_Schermafbeelding%202025-09-28%20195914.png" 
                  alt="Janthiel Beach" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('destinations.janthiel.title')}</h3>
                  <p className="text-gray-600">{t('destinations.janthiel.description')}</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow">
                <img 
                  src="https://images.unsplash.com/photo-1621768827198-8e831c892c5c?w=600&h=400&fit=crop" 
                  alt="Mambo Beach" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('destinations.mambo.title')}</h3>
                  <p className="text-gray-600">{t('destinations.mambo.description')}</p>
                </div>
              </div>
            </div>
          </div>

          <VideoSlideshow />
        </div>
      </div>

      {/* Joyful Features Section */}
      <div className="py-20 bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-10 left-20 w-24 h-24 bg-yellow-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-32 h-32 bg-orange-200 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mb-4"></div>
            <h2 className="text-5xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
              {t('cars.fleetTitle')}
            </h2>
            <p className="text-2xl text-orange-700 max-w-4xl mx-auto font-medium">
              {t('cars.fleetSubtitle')}
            </p>

            {/* Currency selector (requested placement: Cars section header) */}
            <div className="mt-6 flex justify-center">
              <div className="inline-flex rounded-lg border border-orange-200 bg-white/70 backdrop-blur-sm p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => changeCurrency('EUR')}
                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                    currency === 'EUR' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'
                  }`}
                >
                  EUR
                </button>
                <button
                  type="button"
                  onClick={() => changeCurrency('USD')}
                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                    currency === 'USD' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'
                  }`}
                >
                  USD
                </button>
                <button
                  type="button"
                  onClick={() => changeCurrency('XCG')}
                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                    currency === 'XCG' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'
                  }`}
                >
                  XCG
                </button>
              </div>
            </div>

          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('cars.loading')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="homepage-cars-grid">
              {cars.slice(0, 6).map(car => {
                const availability = carAvailability[car.id];
                return (
                  <CarCard 
                    key={car.id} 
                    car={car} 
                    availability={availability}
                    onBookNow={handleBookNow} 
                  />
                );
              })}
            </div>
          )}

          <div className="text-center mt-8">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 px-8 py-3"
              onClick={() => window.location.href = '/cars'}
            >
              {t('cars.viewAll')}
            </Button>
          </div>
          
          {/* Fun vacation indicators */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-8 bg-white/50 backdrop-blur-sm rounded-full px-8 py-4 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-cyan-400 rounded-full"></div>
                <span className="font-semibold text-cyan-700">Endless Beaches</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-400 rounded-full"></div>
                <span className="font-semibold text-orange-700">Perfect Sunsets</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-400 rounded-full"></div>
                <span className="font-semibold text-red-700">Island Vibes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Secret Paradise Spots Section */}
      <div className="py-20 bg-gradient-to-br from-emerald-50 to-teal-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">{t('paradise.title')}</h2>
            <p className="text-xl text-emerald-700 max-w-3xl mx-auto">
              {t('paradise.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Hidden Beaches */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div 
                className="h-48 bg-cover bg-center bg-gradient-to-br from-blue-400 to-cyan-500"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1612425643044-fa658a575344?w=600&h=400&fit=crop')`
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-emerald-900 mb-3">🏖️ {t('paradise.hiddenBeaches.title')}</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p className="mb-3">{t('paradise.hiddenBeaches.description')}</p>
                  <p className="text-xs text-gray-600">{t('paradise.hiddenBeaches.locations')}</p>
                </div>
              </div>
            </div>

            {/* Secret Snorkeling */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div 
                className="h-48 bg-cover bg-center bg-gradient-to-br from-teal-400 to-blue-500"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1658298208155-ab71765747a1?w=600&h=400&fit=crop')`
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-emerald-900 mb-3">🐠 {t('paradise.secretSnorkeling.title')}</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p className="mb-3">{t('paradise.secretSnorkeling.description')}</p>
                  <p className="text-xs text-gray-600">{t('paradise.secretSnorkeling.locations')}</p>
                </div>
              </div>
            </div>

            {/* Sunset Viewpoints */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div 
                className="h-48 bg-cover bg-center bg-gradient-to-br from-orange-400 to-red-500"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1503803548695-c2a7b4a5b875?w=600&h=400&fit=crop')`
                }}
              ></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-emerald-900 mb-3">🌅 {t('paradise.magicalSunsets.title')}</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p className="mb-3">{t('paradise.magicalSunsets.description')}</p>
                  <p className="text-xs text-gray-600">{t('paradise.magicalSunsets.locations')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold max-w-full"
              onClick={() => window.location.href = '/cars'}
            >
              <span className="block text-center leading-snug whitespace-normal">
                🚗 {t('paradise.cta')}
              </span>
            </Button>
            <p className="text-sm text-emerald-600 mt-3">
              {t('paradise.tip')}
            </p>
          </div>
        </div>
      </div>

      {/* Popular Destinations section moved above Cars section */}

      {/* Customer Reviews Carousel */}
      <div className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('reviews.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('reviews.subtitle')}</p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <Carousel className="w-full">
              <CarouselContent>
                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.eva.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.eva.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.eva.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.eva.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.lars.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.lars.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.lars.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.lars.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.anna.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.anna.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.anna.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.anna.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.larsB.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1749003659356-1d1a4451a49d?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.larsB.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.larsB.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.larsB.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.sophie.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1679466061812-211a6b737175?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.sophie.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.sophie.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.sophie.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.daan.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.daan.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.daan.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.daan.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.lotte.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1657128344786-360c3f8e57e5?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.lotte.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.lotte.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.lotte.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.thijs.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1705645930353-0e335311ef20?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.thijs.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.thijs.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.thijs.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.eva.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.pexels.com/photos/30004322/pexels-photo-30004322.jpeg?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.eva.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.eva.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.eva.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{t('reviews.testimonials.lars.quote')}"</p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1566753323558-f4e0952af115?w=80&h=80&fit=crop&crop=face" 
                        alt={t('reviews.testimonials.lars.name')}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{t('reviews.testimonials.lars.name')}</h4>
                        <p className="text-gray-500 text-xs">{t('reviews.testimonials.lars.role')}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              </CarouselContent>
              
              <CarouselPrevious className="hidden md:flex -left-12 bg-white border-2 border-orange-200 hover:bg-orange-50 hover:border-orange-300" />
              <CarouselNext className="hidden md:flex -right-12 bg-white border-2 border-orange-200 hover:bg-orange-50 hover:border-orange-300" />
            </Carousel>
            
            {/* Mobile navigation dots */}
            <div className="flex justify-center mt-8 md:hidden">
              <div className="flex space-x-2">
                {[...Array(Object.keys(translations[language]?.reviews?.testimonials || {}).length || 8)].map((_, i) => (
                  <div key={i} className="w-2 h-2 bg-orange-300 rounded-full"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Features Section */}
      <div className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('services.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('services.subtitle')}</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('services.insurance.title')}</h3>
                  <p className="text-gray-600">{t('services.insurance.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('services.pickup.title')}</h3>
                  <p className="text-gray-600">{t('services.pickup.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('services.service.title')}</h3>
                  <p className="text-gray-600">{t('services.service.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('services.reservation.title')}</h3>
                  <p className="text-gray-600">{t('services.reservation.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('services.cleanCar.title')}</h3>
                  <p className="text-gray-600">{t('services.cleanCar.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('services.family.title')}</h3>
                  <p className="text-gray-600">{t('services.family.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section with Accordion */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('faq.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('faq.subtitle')}</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200 px-6">
                <AccordionTrigger className="text-xl font-bold text-gray-900 hover:text-orange-600 py-6">
                  {t('faq.questions.booking.question')}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6">
                  {t('faq.questions.booking.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 px-6">
                <AccordionTrigger className="text-xl font-bold text-gray-900 hover:text-blue-600 py-6">
                  {t('faq.questions.requirements.question')}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6">
                  {t('faq.questions.requirements.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 px-6">
                <AccordionTrigger className="text-xl font-bold text-gray-900 hover:text-green-600 py-6">
                  {t('faq.questions.payment.question')}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6">
                  {t('faq.questions.payment.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 px-6">
                <AccordionTrigger className="text-xl font-bold text-gray-900 hover:text-purple-600 py-6">
                  {t('faq.questions.insurance.question')}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6">
                  {t('faq.questions.insurance.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 px-6">
                <AccordionTrigger className="text-xl font-bold text-gray-900 hover:text-yellow-600 py-6">
                  {t('faq.questions.fuel.question')}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6">
                  {t('faq.questions.fuel.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl border border-teal-200 px-6">
                <AccordionTrigger className="text-xl font-bold text-gray-900 hover:text-teal-600 py-6">
                  {t('faq.questions.pickup.question')}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6">
                  {t('faq.questions.pickup.answer')}
                </AccordionContent>
              </AccordionItem>


            </Accordion>
          </div>
        </div>
      </div>


      {/* Call to Action */}
      <div className="py-20 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{t('aboutPage.cta.title')}</h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">{t('aboutPage.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={() => window.location.href = '/cars'}
              data-testid="about-browse-cars"
            >
              {t('aboutPage.cta.browseFleet')}
            </Button>
          </div>
        </div>
      </div>

 

      {/* Booking Modal */}
      <BookingModal
        car={selectedCar}
        isOpen={showBookingModal}
        onClose={() => {
          console.log('Closing modal');
          setShowBookingModal(false);
          setSelectedCar(null);
        }}
        onSubmit={handleBookingSubmit}
      />
    </div>
  );
};

// Car Card Component
const CarCard = ({ car, availability, onBookNow }) => {
  const { t } = useLanguage();
  const { currency, formatMoney } = useCurrency();

  // (Reserved for future use)

  const carDescriptionKeyByName = {
    'Budget Explorer': 'budgetExplorer',
    'Island Cruiser': 'islandCruiser',
    'Coastal Comfort': 'coastalComfort',
    'Beach Runner': 'beachRunner'
  };

  const translatedDescKey = carDescriptionKeyByName[car?.name];
  const translatedDescription = translatedDescKey ? t(`carDescriptions.${translatedDescKey}`) : '';
  const descriptionText = translatedDescription && translatedDescription !== `carDescriptions.${translatedDescKey}`
    ? translatedDescription
    : (car.description || '');

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const shortDescription = descriptionText.length > 140 ? `${descriptionText.slice(0, 140)}...` : descriptionText;
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [carCalendarData, setCarCalendarData] = useState(null);

  useEffect(() => {
    if (car && showMiniCalendar) {
      fetchCarCalendar();
    }
  }, [car, currentMonth, showMiniCalendar]);

  const fetchCarCalendar = async () => {
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const response = await axios.get(`${API}/cars/${car.id}/availability`, {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      
      setCarCalendarData(response.data.unavailable_dates || []);
    } catch (error) {
      console.error('Error fetching car calendar:', error);
      setCarCalendarData([]);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getAvailabilityStatus = () => {
    if (!availability) {
      return { status: 'checking', message: 'Checking availability...' };
    }
    
    if (availability.isCurrentlyAvailable) {
      return { status: 'available', message: 'Available Today' };
    } else {
      return { 
        status: 'unavailable', 
        message: `Next available: ${formatDate(availability.nextAvailableDate)}` 
      };
    }
  };

  const getMiniCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateUnavailableInMini = (date) => {
    if (!carCalendarData || !date) return false;
    
    const checkDate = date.toISOString().split('T')[0];
    
    return carCalendarData.some(period => {
      const startDate = period.start_date;
      const endDate = period.end_date;
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const getDateStatusMini = (date) => {
    if (!date) return 'empty';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return 'past';
    if (isDateUnavailableInMini(date)) return 'unavailable';
    return 'available';
  };

  const navigateMiniMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const availabilityInfo = getAvailabilityStatus();
  return (
    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
      <div className="relative h-64 overflow-hidden">
        <img 
          src={car.image_url} 
          alt={car.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <Badge className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500">
          {t(`cars.${car.car_type.toLowerCase()}`)}
        </Badge>
      </div>
      
      <CardHeader>
        <CardTitle className="text-xl">{car.name}</CardTitle>
        <CardDescription className="text-sm">
          {car.brand} {car.model} ·{' '}
          {currency === 'XCG' && XCG_DAILY_RATE_OVERRIDES[car.name]
            ? `XCG ${XCG_DAILY_RATE_OVERRIDES[car.name].low} / XCG ${XCG_DAILY_RATE_OVERRIDES[car.name].high}`
            : `${formatMoney(car.low_season_rate || car.daily_rate)} / ${formatMoney(car.high_season_rate || car.daily_rate)}`
          }
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <p className="text-gray-600 mb-2 text-sm leading-relaxed">
          {isDescriptionExpanded ? descriptionText : shortDescription}
        </p>
        {descriptionText.length > 140 && (
          <button
            type="button"
            onClick={() => setIsDescriptionExpanded((v) => !v)}
            className="text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
          >
            {isDescriptionExpanded ? t('common.readLess') : t('common.readMore')}
          </button>
        )}
        
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span>{car.seats} {t('cars.seats')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-gray-500" />
            <span>{t(`cars.${car.transmission.toLowerCase()}`)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Fuel className="w-4 h-4 text-gray-500" />
            <span>{car.fuel_type}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span>{car.location}</span>
          </div>
        </div>
        
        <div className="border-t pt-4">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">{t('cars.lowSeason')}</div>
                <div className="text-lg font-bold text-green-600">
                  {currency === 'XCG' && XCG_DAILY_RATE_OVERRIDES[car.name]
                    ? `XCG ${XCG_DAILY_RATE_OVERRIDES[car.name].low}`
                    : formatMoney(car.low_season_rate || car.daily_rate)
                  }
                </div>
                <div className="text-xs text-gray-400">May-Jun & Sep-Dec 15</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">{t('cars.highSeason')}</div>
                <div className="text-lg font-bold text-orange-600">
                  {currency === 'XCG' && XCG_DAILY_RATE_OVERRIDES[car.name]
                    ? `XCG ${XCG_DAILY_RATE_OVERRIDES[car.name].high}`
                    : formatMoney(car.high_season_rate || car.daily_rate)
                  }
                </div>
                <div className="text-xs text-gray-400">Jul-Aug & Dec 16-Apr</div>
              </div>
            </div>
            {car.weekly_rate && (
              <div className="text-center mt-2 pt-2 border-t">
                <div className="text-sm text-gray-500">{t('cars.weeklyFrom')}: {formatMoney(car.weekly_rate)}</div>
              </div>
            )}
          </div>
          
          {/* Mini Calendar (collapsible) */}
          <div className="border-t pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowMiniCalendar((v) => !v)}
              className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-orange-600"
            >
              <span>{t('cars.availability')}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showMiniCalendar ? 'rotate-180' : ''}`} />
            </button>

            {showMiniCalendar && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => navigateMiniMonth(-1)}
                      className="p-1 hover:bg-gray-100 rounded text-gray-500"
                    >
                      <ChevronLeft className="w-3 h-3" />
                    </button>
                    <span className="text-xs font-medium text-gray-600 min-w-[80px] text-center">
                      {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigateMiniMonth(1)}
                      className="p-1 hover:bg-gray-100 rounded text-gray-500"
                    >
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Mini Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 text-xs">
                  {/* Day Headers */}
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                    <div key={index} className="text-center text-gray-400 font-medium py-1">
                      {day}
                    </div>
                  ))}

                  {/* Calendar Days */}
                  {getMiniCalendarDays().map((date, index) => {
                    const status = getDateStatusMini(date);

                    return (
                      <div
                        key={index}
                        className={`text-center py-1 text-xs rounded ${
                          status === 'available'
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : status === 'unavailable'
                            ? 'bg-red-100 text-red-700 border border-red-200'
                            : status === 'past'
                            ? 'bg-gray-100 text-gray-400'
                            : ''
                        }`}
                      >
                        {date ? date.getDate() : ''}
                      </div>
                    );
                  })}
                </div>

                {/* Calendar Legend */}
                <div className="flex items-center justify-center gap-3 mt-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-200 border border-green-300 rounded"></div>
                    <span className="text-gray-600">{t('cars.available')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-red-200 border border-red-300 rounded"></div>
                    <span className="text-gray-600">{t('cars.unavailable')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Availability Status */}
          <div className={`mb-4 p-3 rounded-lg text-center text-sm font-medium ${
            availabilityInfo.status === 'available' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : availabilityInfo.status === 'unavailable'
              ? 'bg-orange-100 text-orange-800 border border-orange-200'
              : 'bg-gray-100 text-gray-600 border border-gray-200'
          }`}>
            <div className="flex items-center justify-center gap-2">
              {availabilityInfo.status === 'available' && (
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              )}
              {availabilityInfo.status === 'unavailable' && (
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              )}
              {availabilityInfo.status === 'checking' && (
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
              )}
              {availabilityInfo.message}
            </div>
          </div>
          
          <Button 
            className={`w-full transition-all duration-200 ${
              availabilityInfo.status === 'available'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                : availabilityInfo.status === 'unavailable'
                ? 'bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600'
                : 'bg-gradient-to-r from-gray-300 to-gray-400'
            }`}
            data-testid={`book-car-${car.id}`}
            onClick={() => onBookNow(car)}
            disabled={availabilityInfo.status === 'checking'}
          >
            {availabilityInfo.status === 'available' 
              ? t('cars.bookNow') 
              : availabilityInfo.status === 'unavailable'
              ? t('cars.nextAvailable')
              : t('common.loading')
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Simple Working Booking Modal Component with Calendar
const BookingModal = ({ car, isOpen, onClose, onSubmit }) => {
  const { currency, formatMoney } = useCurrency();

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    street_name: '',
    house_number: '',
    city: '',
    pickup_date: '',
    return_date: '',
    pickup_location: 'Curacao Airport',
    return_location: 'Curacao Airport',
    special_requests: ''
  });

  const [showDatePicker, setShowDatePicker] = useState({ pickup: false, return: false });
  const [totalDays, setTotalDays] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isHighSeason, setIsHighSeason] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDateType, setSelectedDateType] = useState(null);

  // Fetch unavailable dates when modal opens
  useEffect(() => {
    if (isOpen && car) {
      fetchUnavailableDates();
    }
  }, [isOpen, car]);

  const fetchUnavailableDates = async () => {
    if (!car) return;
    
    try {
      const today = new Date();
      const oneYearLater = new Date();
      oneYearLater.setFullYear(today.getFullYear() + 1);
      
      const response = await axios.get(`${API}/cars/${car.id}/availability`, {
        params: {
          start_date: today.toISOString().split('T')[0],
          end_date: oneYearLater.toISOString().split('T')[0]
        }
      });
      
      setUnavailableDates(response.data.unavailable_dates || []);
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
      setUnavailableDates([]);
    }
  };

  // Check if a date is unavailable
  const isDateUnavailable = (date) => {
    return unavailableDates.some(period => {
      const startDate = parseISO(period.start_date);
      const endDate = parseISO(period.end_date);
      return isWithinInterval(date, { start: startDate, end: endDate });
    });
  };

  // Disable unavailable dates and past dates
  const tileDisabled = ({ date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < today) return true;
    
    // Disable unavailable dates
    return isDateUnavailable(date);
  };

  // Add custom class for unavailable dates
  const tileClassName = ({ date }) => {
    if (isDateUnavailable(date)) {
      return 'unavailable-date';
    }
    return '';
  };

  const handleCalendarDateSelect = (date) => {
    const formattedDate = formatDate(date, 'yyyy-MM-dd');
    
    if (selectedDateType === 'pickup') {
      setFormData(prev => ({ ...prev, pickup_date: formattedDate }));
    } else if (selectedDateType === 'return') {
      setFormData(prev => ({ ...prev, return_date: formattedDate }));
    }
    
    setShowCalendar(false);
    setSelectedDateType(null);
  };

  const openCalendar = (type) => {
    setSelectedDateType(type);
    setShowCalendar(true);
  };

  // Function to check if date is in high season
  const checkHighSeason = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    // High season: July 1 – August 31 & December 16 – April 30
    return (
      (month >= 7 && month <= 8) || // July-August
      (month === 12 && day >= 16) || // December 16-31
      (month >= 1 && month <= 4) // January-April
    );
  };

  // Calculate pricing based on dates
  useEffect(() => {
    if (formData.pickup_date && formData.return_date && car) {
      const pickup = new Date(formData.pickup_date);
      const returnDate = new Date(formData.return_date);
      const diffTime = Math.abs(returnDate - pickup);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 0) {
        setTotalDays(diffDays);
        
        // Check if any day in the range is high season
        const isHigh = checkHighSeason(formData.pickup_date) || checkHighSeason(formData.return_date);
        setIsHighSeason(isHigh);
        
        const dailyRate = isHigh ? (car.high_season_rate || car.daily_rate) : (car.low_season_rate || car.daily_rate);
        setTotalAmount(diffDays * dailyRate);
      }
    }
  }, [formData.pickup_date, formData.return_date, car]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.pickup_date || !formData.return_date) {
      alert('Please select pickup and return dates');
      return;
    }
    
    const bookingData = {
      ...formData,
      car_id: car?.id,
      currency: 'EUR',
      pickup_date: new Date(formData.pickup_date).toISOString(),
      return_date: new Date(formData.return_date).toISOString()
    };
    
    onSubmit(bookingData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Generate date options for the next 365 days
  const generateDateOptions = (startDays = 0) => {
    const options = [];
    const today = new Date();
    for (let i = startDays; i < 365; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateString = date.toISOString().split('T')[0];
      const displayDate = date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
      options.push({ value: dateString, label: displayDate });
    }
    return options;
  };

  if (!car) return null;

  const currentRate = isHighSeason ? (car.high_season_rate || car.daily_rate) : (car.low_season_rate || car.daily_rate);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Book Your {car.name}</DialogTitle>
          <DialogDescription>
            Complete your reservation for the {car.brand} {car.model}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name">Full Name *</Label>
              <Input
                id="customer_name"
                data-testid="customer-name-input"
                value={formData.customer_name}
                onChange={(e) => handleChange('customer_name', e.target.value)}
                required
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <Label htmlFor="customer_email">Email *</Label>
              <Input
                id="customer_email"
                type="email"
                data-testid="customer-email-input"
                value={formData.customer_email}
                onChange={(e) => handleChange('customer_email', e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="customer_phone">Phone Number *</Label>
            <Input
              id="customer_phone"
              data-testid="customer-phone-input"
              value={formData.customer_phone}
              onChange={(e) => handleChange('customer_phone', e.target.value)}
              required
              placeholder="+1-555-123-4567"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="street_name">Street name *</Label>
              <Input
                id="street_name"
                data-testid="street-name-input"
                value={formData.street_name}
                onChange={(e) => handleChange('street_name', e.target.value)}
                required
                placeholder="Street"
              />
            </div>
            <div>
              <Label htmlFor="house_number">House number *</Label>
              <Input
                id="house_number"
                data-testid="house-number-input"
                value={formData.house_number}
                onChange={(e) => handleChange('house_number', e.target.value)}
                required
                placeholder="12A"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              data-testid="city-input"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              required
              placeholder="Willemstad"
            />
          </div>

          
          {/* Calendar Date Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Pickup Date *</Label>
              <div className="relative">
                <Select value={formData.pickup_date} onValueChange={(value) => handleChange('pickup_date', value)}>
                  <SelectTrigger data-testid="pickup-date-select" className="w-full">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Select pickup date" />
                  </SelectTrigger>
                  <SelectContent>
                    {generateDateOptions(0).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Return Date *</Label>
              <div className="relative">
                <Select value={formData.return_date} onValueChange={(value) => handleChange('return_date', value)}>
                  <SelectTrigger data-testid="return-date-select" className="w-full">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Select return date" />
                  </SelectTrigger>
                  <SelectContent>
                    {generateDateOptions(1).map((option) => (
                      <SelectItem 
                        key={option.value} 
                        value={option.value}
                        disabled={formData.pickup_date && option.value <= formData.pickup_date}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pickup_location">Pickup Location</Label>
              <Select value={formData.pickup_location} onValueChange={(value) => handleChange('pickup_location', value)}>
                <SelectTrigger data-testid="pickup-location-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Curacao Airport">Curacao Airport</SelectItem>
                  <SelectItem value="Willemstad Downtown">Willemstad Downtown</SelectItem>
                  <SelectItem value="Hotel Delivery">Hotel Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="return_location">Return Location</Label>
              <Select value={formData.return_location} onValueChange={(value) => handleChange('return_location', value)}>
                <SelectTrigger data-testid="return-location-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Curacao Airport">Curacao Airport</SelectItem>
                  <SelectItem value="Willemstad Downtown">Willemstad Downtown</SelectItem>
                  <SelectItem value="Hotel Pickup">Hotel Pickup</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="special_requests">Special Requests</Label>
            <Textarea
              id="special_requests"
              data-testid="special-requests-input"
              value={formData.special_requests}
              onChange={(e) => handleChange('special_requests', e.target.value)}
              placeholder="Any special requirements or requests?"
            />
          </div>
          
          {totalDays > 0 && (
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg space-y-2 border border-orange-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Rental Period:</span>
                <span className="text-lg font-bold">{totalDays} days</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">Season:</span>
                <Badge variant={isHighSeason ? "destructive" : "secondary"} className="px-3 py-1">
                  {isHighSeason ? "High Season" : "Low Season"}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">Daily Rate:</span>
                <span className="text-lg font-bold text-orange-600">{formatMoney(currentRate)}/day</span>
              </div>
              <div className="border-t border-orange-200 pt-2 flex justify-between items-center">
                <span className="text-xl font-bold">Total Amount:</span>
                <span className="text-2xl font-bold text-green-600">{formatMoney(totalAmount)}</span>
              </div>
            </div>
          )}
          
          {/* Payment Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">💳 Payment Options</h4>

            <p className="text-sm text-blue-700 mb-3">
              Secure payment processing powered by <strong>Stripe Payments</strong>:
            </p>
            <div className="space-y-2 text-sm text-blue-700">
              <div className="flex items-center">
                <span className="font-semibold text-orange-600">🇳🇱 iDEAL</span>
                <span className="ml-2">- Direct bank payment (Netherlands, EUR)</span>
              </div>
              <div>💳 <strong>Credit & Debit Cards</strong> - Visa, Mastercard</div>
              <div className="text-xs mt-2 bg-green-100 text-green-700 p-2 rounded">
                ✓ Currency supported: EUR
              </div>
            </div>
            <div className="flex items-center justify-center mt-3 space-x-2">
              <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded font-semibold">iDEAL</span>
              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">VISA</span>
              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">MASTERCARD</span>
              <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-1 rounded font-bold">STRIPE</span>
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600" 
              data-testid="submit-booking-btn"
              disabled={!formData.pickup_date || !formData.return_date}
            >
              Proceed to Payment {totalAmount > 0 ? `(${formatMoney(totalAmount)})` : ''}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Calendar Modal */}
      {showCalendar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Select {selectedDateType === 'pickup' ? 'Pickup' : 'Return'} Date
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCalendar(false)}
              >
                ✕
              </Button>
            </div>
            
            <div className="mb-4">
              <ReactCalendar
                onChange={handleCalendarDateSelect}
                value={null}
                tileDisabled={tileDisabled}
                tileClassName={tileClassName}
                selectRange={false}
                minDate={new Date()}
              />
            </div>
            
            <div className="text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-red-200 border border-red-400"></div>
                <span>Unavailable dates</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-200 border border-green-400"></div>
                <span>Available dates</span>
              </div>
            </div>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowCalendar(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

// Cars Page Component
const CarsPage = () => {
  const { t } = useLanguage();
  const { currency, changeCurrency } = useCurrency();


  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carAvailability, setCarAvailability] = useState({});
  const [filters, setFilters] = useState({
    car_type: '',
    transmission: '',
    min_seats: '',
    max_daily_rate: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  useEffect(() => {
    const fetchCarsAndAvailability = async () => {
      try {
        setLoading(true);
        
        // Fetch cars
        const carsResponse = await axios.get(`${API}/cars`);
        const carsData = carsResponse.data;
        setCars(carsData);
        
        // Fetch availability for each car (next 30 days)
        const today = new Date();
        const thirtyDaysLater = new Date();
        thirtyDaysLater.setDate(today.getDate() + 30);
        
        const availabilityPromises = carsData.map(async (car) => {
          try {
            const response = await axios.get(`${API}/cars/${car.id}/availability`, {
              params: {
                start_date: today.toISOString().split('T')[0],
                end_date: thirtyDaysLater.toISOString().split('T')[0]
              }
            });
            
            // Calculate next available date
            const unavailableDates = response.data.unavailable_dates || [];
            const nextAvailableDate = findNextAvailableDate(today, unavailableDates);
            
            return {
              carId: car.id,
              unavailableDates,
              nextAvailableDate,
              isCurrentlyAvailable: isDateAvailable(today, unavailableDates)
            };
          } catch (error) {
            console.error(`Error fetching availability for car ${car.id}:`, error);
            return {
              carId: car.id,
              unavailableDates: [],
              nextAvailableDate: today,
              isCurrentlyAvailable: true
            };
          }
        });
        
        const availabilityResults = await Promise.all(availabilityPromises);
        const availabilityMap = {};
        availabilityResults.forEach(result => {
          availabilityMap[result.carId] = result;
        });
        
        setCarAvailability(availabilityMap);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching cars:', error);
        setLoading(false);
      }
    };

    fetchCarsAndAvailability();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [cars, filters, searchTerm]);

  // Helper function to check if a date is available
  const isDateAvailable = (date, unavailableDates) => {
    const checkDate = new Date(date);
    return !unavailableDates.some(period => {
      const startDate = new Date(period.start_date);
      const endDate = new Date(period.end_date);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Helper function to find next available date
  const findNextAvailableDate = (startDate, unavailableDates) => {
    let currentDate = new Date(startDate);
    const maxDays = 90; // Check up to 90 days ahead
    
    for (let i = 0; i < maxDays; i++) {
      if (isDateAvailable(currentDate, unavailableDates)) {
        return currentDate;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // If no availability found in 90 days, return date 90 days from now
    const fallbackDate = new Date(startDate);
    fallbackDate.setDate(fallbackDate.getDate() + 90);
    return fallbackDate;
  };

  const applyFilters = () => {
    let filtered = cars;

    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(car => 
        car.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.model.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.car_type) {
      filtered = filtered.filter(car => car.car_type === filters.car_type);
    }
    if (filters.transmission) {
      filtered = filtered.filter(car => car.transmission === filters.transmission);
    }
    if (filters.min_seats) {
      filtered = filtered.filter(car => car.seats >= parseInt(filters.min_seats));
    }
    if (filters.max_daily_rate) {
      const divisor = currency === 'EUR' ? USD_TO_EUR_RATE : currency === 'XCG' ? USD_TO_XCG_RATE : 1;
      const maxUsd = parseFloat(filters.max_daily_rate) / divisor;
      filtered = filtered.filter(car => car.daily_rate <= maxUsd);
    }

    setFilteredCars(filtered);
  };

  const handleBookNow = (car) => {
    console.log('Opening booking modal for:', car.name);
    setSelectedCar(car);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      console.log('Submitting booking:', bookingData);
      const response = await axios.post(`${API}/bookings`, bookingData);
      console.log('Booking response:', response.data);
      
      // Get the booking ID and proceed to payment
      const bookingId = response.data.id;
      
      // Stripe is primary payment provider
      const paymentProvider = 'stripe';

      // Create Stripe Checkout session
      const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: bookingId,
        street_name: bookingData.street_name,
        house_number: bookingData.house_number,
        city: bookingData.city
      });

      console.log('Stripe payment response:', paymentResponse.data);

      // Store booking ID and payment provider in localStorage for return flow
      localStorage.setItem('pendingBookingId', bookingId);
      localStorage.setItem('paymentProvider', paymentProvider);

      // Redirect to Stripe checkout
      window.location.href = paymentResponse.data.checkout_url;
      
    } catch (error) {
      console.error('Booking error:', error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        // FastAPI/Pydantic validation errors
        toast.error(detail.map((d) => d?.msg).filter(Boolean).join(' | ') || 'Booking failed');
      } else {
        toast.error(detail || 'Failed to create booking. Please try again.');
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      car_type: '',
      transmission: '',
      min_seats: '',
      max_daily_rate: ''
    });
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading our amazing fleet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Our Fleet</h1>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/'}
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search cars by name, brand, or model..."

                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-cars-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <Select value={filters.car_type} onValueChange={(value) => setFilters(prev => ({...prev, car_type: value}))}>
              <SelectTrigger data-testid="car-type-filter">
                <SelectValue placeholder="Car Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sedan">Sedan</SelectItem>
                <SelectItem value="suv">SUV</SelectItem>
                <SelectItem value="convertible">Convertible</SelectItem>
                <SelectItem value="luxury">Luxury</SelectItem>
                <SelectItem value="compact">Compact</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.transmission} onValueChange={(value) => setFilters(prev => ({...prev, transmission: value}))}>
              <SelectTrigger data-testid="transmission-filter">
                <SelectValue placeholder="Transmission" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="automatic">Automatic</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>

          {/* Currency selector */}
          <div className="mb-6 flex justify-center">
            <div className="inline-flex rounded-lg border border-orange-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => changeCurrency('EUR')}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                  currency === 'EUR' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'
                }`}
              >
                EUR
              </button>
              <button
                type="button"
                onClick={() => changeCurrency('USD')}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                  currency === 'USD' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'
                }`}
              >
                USD
              </button>
              <button
                type="button"
                onClick={() => changeCurrency('XCG')}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                  currency === 'XCG' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'
                }`}
              >
                XCG
              </button>
            </div>
          </div>

            
            <Select value={filters.min_seats} onValueChange={(value) => setFilters(prev => ({...prev, min_seats: value}))}>
              <SelectTrigger data-testid="seats-filter">
                <SelectValue placeholder="Min Seats" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2+ Seats</SelectItem>
                <SelectItem value="4">4+ Seats</SelectItem>
                <SelectItem value="5">5+ Seats</SelectItem>
                <SelectItem value="7">7+ Seats</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              type="number"
              placeholder="Max Daily Rate ($)"
              value={filters.max_daily_rate}
              onChange={(e) => setFilters(prev => ({...prev, max_daily_rate: e.target.value}))}
              data-testid="price-filter"
            />
          </div>
          
          <div className="flex justify-between items-center">
            <p className="text-gray-600">{filteredCars.length} cars available</p>
            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Cars Grid */}
        {filteredCars.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No cars found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria or filters</p>
            <Button onClick={clearFilters} variant="outline">
              Clear All Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cars-grid">
            {filteredCars.map(car => {
              const availability = carAvailability[car.id];
              return (
                <CarCard 
                  key={car.id} 
                  car={car} 
                  availability={availability}
                  onBookNow={handleBookNow} 
                />
              );
            })}
          </div>
        )}
      </div>


      {/* Booking Information */}
      <div className="container mx-auto px-6 pb-16">
        <div className="mt-12 bg-white rounded-lg shadow-sm border p-8" data-testid="cars-booking-info">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('carsPage.bookingInfo.title')}</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact */}
            <div className="lg:col-span-1">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('carsPage.bookingInfo.bookWithUs')}</h3>

              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold text-gray-700">{t('carsPage.bookingInfo.whatsapp')}</div>
                  <div className="text-gray-900">(+31) 0651929275</div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700">{t('carsPage.bookingInfo.email')}</div>
                  <div className="text-gray-900">Info@curacruze.com</div>
                </div>

                <div className="text-sm text-gray-700">
                  {t('carsPage.bookingInfo.reservationFee')}
                </div>
              </div>
            </div>

            {/* Policies */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('carsPage.bookingInfo.cancellationPolicy')}</h3>
                <ul className="space-y-2 text-gray-700 list-disc pl-5">
                  <li>{t('carsPage.bookingInfo.cancel48')}</li>
                  <li>{t('carsPage.bookingInfo.cancel48to24')}</li>
                  <li>{t('carsPage.bookingInfo.cancel24')}</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('carsPage.bookingInfo.rentalTerms')}</h3>
                <ul className="space-y-2 text-gray-700 list-disc pl-5">
                  <li>{t('carsPage.bookingInfo.termNoDeposit')}</li>
                  <li>{t('carsPage.bookingInfo.termCleanFullTank')}</li>
                  <li>{t('carsPage.bookingInfo.termInsured')}</li>
                  <li>{t('carsPage.bookingInfo.termPickupDropoff')}</li>
                  <li>{t('carsPage.bookingInfo.termExtras')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>


 


      {/* Booking Modal */}
      <BookingModal
        car={selectedCar}
        isOpen={showBookingModal}
        onClose={() => {
          console.log('Closing modal');
          setShowBookingModal(false);
          setSelectedCar(null);
        }}
        onSubmit={handleBookingSubmit}
      />
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { t, language, changeLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16 gap-3">
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_island-cruiser/artifacts/uyqujm9j_Black%20Flat%20Illustrative%20Car%20Rental%20Service%20Logo%20%281%29.png" 
              alt="CuraCruze logo" 
              className="h-12 w-12 sm:h-16 sm:w-16 mr-2 sm:mr-3 object-contain"
            />
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-6">
            <a href="/" className="text-gray-700 hover:text-orange-500 transition-colors font-medium" data-testid="nav-home">
              {t('nav.home')}
            </a>
            <a href="/cars" className="text-gray-700 hover:text-orange-500 transition-colors font-medium" data-testid="nav-cars">
              {t('nav.cars')}
            </a>
            <a href="/calendar" className="text-gray-700 hover:text-orange-500 transition-colors font-medium" data-testid="nav-calendar">
              {t('nav.calendar')}
            </a>
            <a href="/about" className="text-gray-700 hover:text-orange-500 transition-colors font-medium" data-testid="nav-about">
              {t('nav.about')}
            </a>

            {/* Language Switcher */}
            <div className="relative group">
              <button className="flex items-center space-x-2 text-gray-700 hover:text-orange-500 transition-colors font-medium px-3 py-2 rounded-md">
                <span>{currentLanguage?.flag}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center space-x-3 px-4 py-3 text-left hover:bg-orange-50 transition-colors w-full ${
                      language === lang.code ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <div>
                      <div className="font-medium">{lang.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <Button 
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600" 
              data-testid="nav-book-now"
              onClick={() => window.location.href = '/cars'}
            >
              {t('nav.bookNow')}
            </Button>
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <Button
              size="sm"
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              onClick={() => window.location.href = '/cars'}
            >
              {t('nav.bookNow')}
            </Button>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-gray-700 hover:bg-orange-50"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="mt-2 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
              <a href="/" className="block px-4 py-3 text-gray-700 hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.home')}
              </a>
              <a href="/cars" className="block px-4 py-3 text-gray-700 hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.cars')}
              </a>
              <a href="/calendar" className="block px-4 py-3 text-gray-700 hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.calendar')}
              </a>
              <a href="/about" className="block px-4 py-3 text-gray-700 hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.about')}
              </a>


              {/* Mobile language picker */}
              <div className="border-t border-gray-100 p-3">
                <div className="text-xs font-semibold text-gray-500 mb-2">{t('nav.language')}</div>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                        language === lang.code
                          ? 'border-orange-300 bg-orange-50 text-orange-700'
                          : 'border-gray-200 bg-white text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Main App Component
function App() {
  // useEffect for initialization
  useEffect(() => {
    // Any other initialization code can go here if needed
  }, []);

  // Removed chat functionality as requested

  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <Navigation />
          <div className="pt-16 min-h-screen flex flex-col"> {/* Offset for fixed navigation */}
            <div className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/cars" element={<CarsPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
                <Route path="/admin" element={<AdminDashboardPage />} />
              </Routes>
            </div>
            <SiteFooter />
          </div>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
